/* DaLuca ready-made editorial PDP — repaired DOM organizer.
   Uses the existing working product controls, but builds clean specs and a fixed-width card. */
(function(){
  'use strict';

  function clean(v){ return String(v || '').replace(/\s+/g,' ').trim(); }
  function root(){ return document.querySelector('.daluca-pdp--ready-editorial[data-daluca-ready-editorial="true"]:not(.daluca-pdp--custom)'); }
  function body(step){ return step && (step.querySelector('.daluca-concierge-step__body') || step); }
  function setTitle(step,title){ const h=step && step.querySelector('h2,h3,h4'); if(h) window.DaLucaI18n.setText(h, title); }
  function setStepNumber(step,num){
    if(!step) return;
    const head=step.querySelector('.daluca-concierge-step__head') || step;
    const candidates=Array.from(head.querySelectorAll('span,small,div,p'))
      .filter(el=>/^0?[1-4]$/.test(clean(window.DaLucaI18n.sourceText(el))));
    if(candidates.length) window.DaLucaI18n.setText(candidates[0], String(num).padStart(2,'0'));
  }

  function keyRegex(key){
    return new RegExp('^\\s*'+key.replace(/[.*+?^${}()|[\\]\\]/g,'\\$&')+'\\s*[:\\-]?\\s*$','i');
  }

  function findExactLabel(story,key){
    const re=keyRegex(key);
    const nodes=Array.from(story.querySelectorAll('*'));
    return nodes.find(el=>{
      if(el.closest('.daluca-ready-story-specs__specs')) return false;
      if(el.children.length>4) return false;
      return re.test(clean(window.DaLucaI18n.productSourceText(el)));
    }) || null;
  }

  function sourceForKey(story,key){
    const label=findExactLabel(story,key);
    if(!label) return null;

    const definition=label.matches('dt') ? label.nextElementSibling : null;
    if(definition?.matches('dd') && clean(window.DaLucaI18n.productSourceText(definition))){
      return {value:clean(window.DaLucaI18n.productDisplayText(definition)),sources:[label,definition]};
    }

    const allKeys=['Length','Width','Thickness','Material','Hardware','Buckle','Features'];
    const otherKeys=allKeys.filter(k=>k.toLowerCase()!==key.toLowerCase());

    function validValue(value){
      value=clean(value);
      if(!value || value.toLowerCase()===key.toLowerCase() || value.length>180) return false;

      // A real single-spec value must never contain another specification label.
      const lower=value.toLowerCase();
      if(otherKeys.some(k=>lower.includes(k.toLowerCase()))) return false;
      return true;
    }

    // 1) Most reliable: label and value are direct siblings.
    const next=label.nextElementSibling;
    if(next){
      const value=clean(window.DaLucaI18n.productSourceText(next));
      if(validValue(value)) return {value:clean(window.DaLucaI18n.productDisplayText(next)),sources:[label,next]};
    }

    // 2) Label may be wrapped inside one child and value is another direct child
    //    of the same row.
    const row=label.parentElement;
    if(row){
      const direct=Array.from(row.children).filter(el=>el!==label);
      for(const child of direct){
        const value=clean(window.DaLucaI18n.productSourceText(child));
        if(validValue(value) && !keyRegex(key).test(value)){
          return {value:clean(window.DaLucaI18n.productDisplayText(child)),sources:[row]};
        }
      }
    }

    // 3) Some themes wrap label and value in adjacent sibling boxes.
    const labelBox=label.parentElement;
    if(labelBox && labelBox.nextElementSibling){
      const value=clean(window.DaLucaI18n.productSourceText(labelBox.nextElementSibling));
      if(validValue(value) && !keyRegex(key).test(value)){
        return {value:clean(window.DaLucaI18n.productDisplayText(labelBox.nextElementSibling)),sources:[labelBox,labelBox.nextElementSibling]};
      }
    }

    // 4) Conservative row fallback. Only accept parent text when it contains
    //    this spec and NONE of the other known spec labels.
    let parent=label.parentElement;
    for(let hop=0; parent && parent!==story && hop<3; hop++,parent=parent.parentElement){
      const txt=clean(window.DaLucaI18n.productSourceText(parent));
      const lower=txt.toLowerCase();

      if(otherKeys.some(k=>lower.includes(k.toLowerCase()))) continue;
      if(!lower.startsWith(key.toLowerCase())) continue;

      const value=clean(txt.slice(key.length).replace(/^\s*[:\-]?\s*/,''));
      if(validValue(value)) return {value:window.DaLucaI18n.locale !== 'en' ? (window.DaLucaI18n.productField(parent)?.value || value) : value,sources:[parent]};
    }

    return null;
  }

  function extractSpecs(story){
    const keys=['Length','Width','Thickness','Material','Hardware','Buckle','Features'];
    const rows=[];
    const sources=[];

    keys.forEach(key=>{
      const found=sourceForKey(story,key);
      if(found && found.value){
        rows.push([key,found.value]);
        found.sources.forEach(node=>{ if(node && !sources.includes(node)) sources.push(node); });
      }
    });

    sources.forEach(node=>node.classList.add('daluca-ready-spec-source'));
    return rows;
  }

  function findWholeSpecBlock(story){
    const keys=['length','width','thickness','material','hardware','buckle','features'];
    const candidates=Array.from(story.querySelectorAll('table,dl,ul,ol,div'))
      .filter(el=>{
        if(el===story || el.closest('.daluca-ready-story-specs__specs')) return false;
        const t=clean(window.DaLucaI18n.productSourceText(el)).toLowerCase();
        const hits=keys.filter(k=>t.includes(k)).length;
        return hits>=5 && t.length<900;
      })
      .sort((a,b)=>clean(window.DaLucaI18n.productSourceText(a)).length-clean(window.DaLucaI18n.productSourceText(b)).length);
    return candidates[0] || null;
  }

  function findExactStrapNote(story){
    const nodes=Array.from(story.querySelectorAll('p,em,i,small,div'));
    return nodes.find(el=>/^you receive the exact strap pictured\.?$/i.test(clean(window.DaLucaI18n.productSourceText(el)))) || null;
  }

  function buildSpecs(sourceScope){
    const wrap=document.createElement('div');
    wrap.className='daluca-ready-story-specs__specs';
    window.DaLucaI18n.setHtml(wrap, '<span class="daluca-ready-story-specs__eyebrow">Specifications</span>');

    let rows=extractSpecs(sourceScope);

    // Ready-made one-piece leather military straps use several Horween leathers.
    // Never label the family generically as Shell Cordovan just because legacy
    // description/spec markup happens to contain that phrase.
    const readyFamily=String(root()?.dataset.dalucaReadyFamily || '');
    if(readyFamily==='leather-military'){
      let hasMaterial=false;
      rows=rows.map(([key,value])=>{
        if(String(key).toLowerCase()==='material'){
          hasMaterial=true;
          return [key,'Genuine Horween Leather'];
        }
        return [key,value];
      });
      if(!hasMaterial) rows.push(['Material','Genuine Horween Leather']);
    }

    if(readyFamily==='perlon') rows.push(['Origin','Made in Europe']);

    // The legacy FKM description can retain this unlabelled feature after
    // its other details have moved. Keep it with the specifications.
    let quickReleaseNote=false;
    if(readyFamily==='fkm'){
      const legacy=document.querySelector('.daluca-product-details');
      const sources=legacy ? Array.from(legacy.querySelectorAll('.daluca-product-details__description li'))
        .filter(el=>/^Quick Release Spring Bars\s*\(/i.test(clean(window.DaLucaI18n.sourceText(el)))) : [];
      if(sources.length){
        quickReleaseNote=true;
        rows.push(['Spring bars','Quick release']);
        sources.forEach(el=>el.remove());
        if(!clean(window.DaLucaI18n.sourceText(legacy)) && !legacy.querySelector('img,video,iframe')){
          legacy.hidden=true;
          legacy.style.setProperty('display','none','important');
        }
      }
    }

    const whole=findWholeSpecBlock(sourceScope);
    if(whole) whole.classList.add('daluca-ready-source-spec-block-hidden');

    const dl=document.createElement('dl');
    dl.className='daluca-ready-specs-list';

    const finalRows=rows.length ? rows : [
      ['Width',root().dataset.dalucaProductWidths ? root().dataset.dalucaProductWidths.split(',').join(' / ')+'mm' : 'See product details'],
      ['Material',root().dataset.dalucaProductMaterial || 'Premium material']
    ];

    finalRows.forEach(([k,v])=>{
      const row=document.createElement('div');
      row.className='daluca-ready-specs-row';
      window.DaLucaI18n.setHtml(row, '<dt>'+k+'</dt><dd>'+v+'</dd>');
      dl.appendChild(row);
    });
    wrap.appendChild(dl);

    if(quickReleaseNote){
      const installation=document.createElement('p');
      installation.className='daluca-ready-specs-note';
      window.DaLucaI18n.setText(installation, 'Tool-free installation. A spring bar tool may be needed to remove your current strap.');
      wrap.appendChild(installation);
    }

    const sourceNote=findExactStrapNote(sourceScope);
    if(sourceNote) sourceNote.classList.add('daluca-ready-source-note');

    const note=document.createElement('p');
    note.className='daluca-ready-specs-note';
    const family=String(root()?.dataset.dalucaReadyFamily || '');
    window.DaLucaI18n.setText(note, family==='leather-military'
      ? 'Choose your width and leather color in Step 02.'
      : root()?.classList.contains('daluca-pdp--ooak') ? 'You receive the exact strap pictured.' : (root()?.querySelector('daluca-rally-width') ? window.DaLucaFinderHandoff?.text.length_width : window.DaLucaFinderHandoff?.text.variant_note) || '');
    wrap.appendChild(note);
    return wrap;
  }

  function countExactSpecLabels(scope){
    if(!scope) return 0;
    const keys=['Length','Width','Thickness','Material','Hardware','Buckle','Features'];
    return keys.reduce((count,key)=>{
      const re=keyRegex(key);
      const found=Array.from(scope.querySelectorAll('*')).some(el=>{
        if(el.children.length>4) return false;
        return re.test(clean(window.DaLucaI18n.productSourceText(el)));
      });
      return count+(found?1:0);
    },0);
  }

  function findStoryNode(info){
    if(!info) return null;

    // Standard wrapper used by many ready-made products.
    const standard=info.querySelector('.daluca-concierge-story');
    if(standard){
      standard.classList.add('daluca-ready-story-node');
      return standard;
    }

    const nodes=Array.from(info.querySelectorAll('span,p,div,h1,h2,h3,h4,h5,h6'));
    const eyebrow=nodes.find(el=>{
      const t=clean(window.DaLucaI18n.productSourceText(el)).toLowerCase();
      return t==='the story of this strap' || t==='the story of this vintage strap';
    });

    if(eyebrow){
      let candidate=eyebrow.parentElement;
      let fallback=null;

      for(let hop=0; candidate && candidate!==info && hop<9; hop++,candidate=candidate.parentElement){
        const text=clean(window.DaLucaI18n.productSourceText(candidate));
        const hasSteps=!!candidate.querySelector('.daluca-concierge-step');
        if(hasSteps) continue;

        const hasLongStory=Array.from(candidate.querySelectorAll('p,div'))
          .some(el=>{
            const t=clean(window.DaLucaI18n.productSourceText(el));
            return t.length>180 && t.length<5000;
          });

        const specHits=countExactSpecLabels(candidate);
        const hasExactNote=Array.from(candidate.querySelectorAll('p,em,i,small,div'))
          .some(el=>/^you receive the exact strap pictured\.?$/i.test(clean(window.DaLucaI18n.productSourceText(el))));

        // Critical vintage fix:
        // DO NOT require a "watch band/watch strap" story heading.
        // Products such as Akten render:
        // eyebrow -> long description -> specs, with no heading at all.
        if(
          text.length>160 &&
          text.length<8500 &&
          hasLongStory &&
          (specHits>=3 || hasExactNote)
        ){
          candidate.classList.add('daluca-ready-story-node');
          return candidate;
        }

        // Keep the smallest reasonable Story-only wrapper as a fallback.
        if(!fallback && hasLongStory && text.length<6500){
          fallback=candidate;
        }
      }

      if(fallback){
        fallback.classList.add('daluca-ready-story-node');
        return fallback;
      }
    }

    // Last fallback: find the long product-story paragraph and use the
    // smallest non-step ancestor. A vintage story title is NOT required.
    const paragraphs=Array.from(info.querySelectorAll('p'))
      .filter(p=>{
        const t=clean(window.DaLucaI18n.productSourceText(p));
        return t.length>180 && t.length<5000;
      })
      .sort((a,b)=>clean(window.DaLucaI18n.productSourceText(b)).length-clean(window.DaLucaI18n.productSourceText(a)).length);

    if(paragraphs.length){
      let candidate=paragraphs[0].parentElement;
      let fallback=null;

      for(let hop=0; candidate && candidate!==info && hop<6; hop++,candidate=candidate.parentElement){
        const text=clean(window.DaLucaI18n.productSourceText(candidate));
        if(candidate.querySelector('.daluca-concierge-step')) continue;

        const specHits=countExactSpecLabels(candidate);
        if(specHits>=3 && text.length<8500){
          candidate.classList.add('daluca-ready-story-node');
          return candidate;
        }

        if(!fallback && text.length<6500) fallback=candidate;
      }

      if(fallback){
        fallback.classList.add('daluca-ready-story-node');
        return fallback;
      }
    }

    return null;
  }

  function ensureVintageStoryHeading(story){
    if(!story) return;

    const existing=Array.from(story.querySelectorAll('h1,h2,h3,h4'))
      .find(el=>{
        const t=clean(window.DaLucaI18n.productSourceText(el));
        return /watch band|watch strap/i.test(t);
      });
    if(existing) return;

    const eyebrow=Array.from(story.querySelectorAll('span,p,div,h5,h6'))
      .find(el=>{
        const t=clean(window.DaLucaI18n.productSourceText(el)).toLowerCase();
        return t==='the story of this strap' || t==='the story of this vintage strap';
      });

    const h=document.createElement('h2');
    h.className='daluca-ready-vintage-story-title';
    window.DaLucaI18n.setText(h, 'The Da Luca One Of A Kind Vintage Watch Band');

    if(eyebrow) eyebrow.insertAdjacentElement('afterend',h);
    else story.insertBefore(h,story.firstChild);
  }

  function storySpecs(r,info,steps){
    let grid=r.querySelector('.daluca-ready-story-specs');
    if(grid) return grid;

    const vintage=isVintageReady(r);

    // Normal ready-made Story is usually inside product-information--inner.
    // Vintage Story can be rendered elsewhere inside the product root by the
    // existing concierge code, so search the full root when needed.
    let story=findStoryNode(info);
    if(!story && vintage) story=findStoryNode(r);
    if(!story) story=findStoryNode(r);
    if(!story) return null;

    if(vintage) ensureVintageStoryHeading(story);

    grid=document.createElement('section');
    grid.className='daluca-ready-story-specs';
    if(vintage) grid.classList.add('daluca-ready-story-specs--vintage');

    const left=document.createElement('div');
    left.className='daluca-ready-story-specs__story';

    // Vintage specification rows can also live outside product-information--inner.
    // Harvest from the entire ready-made root for vintage.
    const specScope=vintage ? r : info;
    const specs=buildSpecs(specScope);

    const guide=r.querySelector('.daluca-gallery-guides');
    if(guide) specs.appendChild(guide);
    left.appendChild(story);
    grid.append(left,specs);
    r.insertBefore(grid,steps || null);

    if(vintage){
      r.dataset.dalucaVintageStoryGrid='true';
    }
    return grid;
  }

  function makeWidthCard(r,step){
    if(!step) return;
    const b=body(step);
    if(!b) return;

    const family=String(r.dataset.dalucaReadyFamily || '');
    const variants=b.querySelector('variant-selects');

    // Managed ready-made families should use the REAL Shopify Width / Color
    // selectors rather than replacing them with a decorative width card.
    if(variants){
      step.classList.add('daluca-ready-step--variant-master');
      setTitle(step, (family==='leather-military' || family==='nylon' || family==='perlon') ? 'Choose Width & Color' : 'Choose Your Strap');

      let intro=b.querySelector('.daluca-ready-variant-master__intro');
      if(family==='nylon' || family==='perlon'){
        if(intro) intro.remove();
      } else if(!intro){
        intro=document.createElement('p');
        intro.className='daluca-ready-variant-master__intro';
        window.DaLucaI18n.setText(intro, window.DaLucaI18n.sourceText(variants).includes('Watch Strap Length')
          ? (window.DaLucaFinderHandoff?.text.length_width || 'Select the length and exact lug width for your watch.')
          : family==='leather-military'
          ? 'Select the exact lug width and leather color for your watch.'
          : 'Select the available width and color for your watch.');
        variants.insertAdjacentElement('beforebegin',intro);
      }

      const kicker=b.querySelector('.daluca-concierge-kicker');
      if(kicker) kicker.style.setProperty('display','none','important');

      const skip=step.querySelector('.daluca-concierge-step__skip');
      if(skip) skip.remove();

      // Defensive cleanup if an older pass already injected a static width card.
      b.querySelectorAll('.daluca-ready-width-card').forEach(n=>n.remove());
      return;
    }

    setTitle(step,'Strap Width');
    if(b.querySelector('.daluca-ready-width-card')) return;

    let widths=clean(r.dataset.dalucaProductWidths).split(',').map(v=>clean(v)).filter(Boolean);
    let value=widths.length ? widths.join(' / ')+'mm' : 'Fixed width';

    window.DaLucaI18n.setHtml(b, '<div class="daluca-ready-width-card"><strong>'+value+'</strong><span>Finished width</span></div>');
    const skip=step.querySelector('.daluca-concierge-step__skip');
    if(skip) skip.remove();
  }

  function moneyFromCents(cents){
    const n=Number(cents);
    if(!Number.isFinite(n)) return '$5.00';
    return '$'+(n/100).toFixed(2);
  }

  function parseMoneyCents(value){
    const m=String(value || '').replace(/,/g,'').match(/(-?\d+(?:\.\d{1,2})?)/);
    if(!m) return 0;
    return Math.round(Number(m[1]) * 100) || 0;
  }

  function completeOrderStep(r){
    if(!r) return null;
    const steps=Array.from(r.querySelectorAll('.daluca-ready-steps > .daluca-concierge-step'));
    if(steps.length>=4) return steps[3];
    return Array.from(r.querySelectorAll('.daluca-concierge-step')).find(step=>/complete your order/i.test(clean(window.DaLucaI18n.sourceText(step)))) || null;
  }

  function completeOrderPriceNode(r){
    const step=completeOrderStep(r);
    if(!step) return null;
    const nodes=Array.from(step.querySelectorAll('*')).filter(el=>{
      if(el.children.length) return false;
      const t=clean(window.DaLucaI18n.sourceText(el));
      return /^\$\s*[\d,.]+(?:\s*USD)?$/i.test(t);
    });
    return nodes[0] || null;
  }

  function updateSpringBarsDisplayedTotal(r,added,extraCents){
    if(!r) return;
    const step=completeOrderStep(r);
    const price=completeOrderPriceNode(r);
    if(!step || !price) return;

    if(!step.dataset.dalucaBasePriceCents){
      step.dataset.dalucaBasePriceCents=String(parseMoneyCents(window.DaLucaI18n.sourceText(price)));
      step.dataset.dalucaBasePriceHadUsd=/USD/i.test(window.DaLucaI18n.sourceText(price)) ? 'true' : 'false';
    }

    const base=Number(step.dataset.dalucaBasePriceCents) || 0;
    const extra=added ? (Number(extraCents) || parseMoneyCents(r.dataset.dalucaSpringBarsPrice) || 500) : 0;
    const total=Math.max(0,base+extra);
    const suffix=step.dataset.dalucaBasePriceHadUsd==='true' ? ' USD' : '';
    window.DaLucaI18n.setText(price, '$'+(total/100).toFixed(2)+suffix);

    let note=step.querySelector('.daluca-ready-order-extra-note');
    if(added){
      if(!note){
        note=document.createElement('small');
        note.className='daluca-ready-order-extra-note';
        price.insertAdjacentElement('afterend',note);
      }
      window.DaLucaI18n.setText(note, 'Includes +$'+(extra/100).toFixed(2)+' extra spring bars');
    }else if(note){
      note.remove();
    }
  }

  function markSpringBarsAdded(r,row,btn,extraCents){
    row.classList.add('is-added');
    row.classList.remove('is-unavailable','is-add-error');
    const check=row.querySelector('.daluca-ready-addon-row__check');
    if(check) window.DaLucaI18n.setText(check, '✓');
    if(btn){
      btn.disabled=false;
      window.DaLucaI18n.setText(btn, 'Remove');
      btn.setAttribute('aria-label','Remove extra standard spring bars');
    }
    updateSpringBarsDisplayedTotal(r,true,extraCents);
  }

  function markSpringBarsRemoved(r,row,btn){
    if(!row) return;
    row.classList.remove('is-added','is-unavailable','is-add-error');
    const check=row.querySelector('.daluca-ready-addon-row__check');
    if(check) window.DaLucaI18n.setText(check, '');
    if(btn){
      btn.disabled=false;
      btn.hidden=false;
      window.DaLucaI18n.setText(btn, 'Add +');
      btn.setAttribute('aria-label','Add extra standard spring bars');
    }
    updateSpringBarsDisplayedTotal(r,false,0);
  }

  function springBarOwner(r){ return r?.dataset.handle || location.pathname.split('/products/')[1]?.split('/')[0] || ''; }
  function ownedSpringBars(item,r){ return item.properties?.['_DaLuca add-on']==='Extra Standard Spring Bars' && item.properties?.['_DaLuca add-on for']===springBarOwner(r); }

  async function removeSpringBarsFromCart(r,row,btn,variant){
    const shopRoot=(window.Shopify && Shopify.routes && Shopify.routes.root) ? Shopify.routes.root : '/';
    const cart=await fetch(shopRoot+'cart.js',{headers:{'Accept':'application/json'}}).then(x=>x.json());
    const item=(cart.items || []).find(x=>
      String(x.variant_id || x.id)===String(variant) &&
      ownedSpringBars(x,r)
    );

    if(item){
      const res=await fetch(shopRoot+'cart/change.js',{
        method:'POST',
        headers:{'Content-Type':'application/json','Accept':'application/json'},
        body:JSON.stringify({id:item.key || item.id,quantity:Math.max(0,Number(item.quantity||1)-1)})
      });
      let payload=null;
      try{ payload=await res.json(); }catch(e){}
      if(!res.ok){
        const message=(payload && (payload.description || payload.message)) || ('Cart remove failed ('+res.status+')');
        throw new Error(message);
      }
    }

    markSpringBarsRemoved(r,row,btn);

    try{
      const refreshed=await fetch(shopRoot+'cart.js',{headers:{'Accept':'application/json'}}).then(x=>x.json());
      document.dispatchEvent(new CustomEvent('cart:updated',{bubbles:true,detail:{cart:refreshed}}));
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh',{bubbles:true,detail:{cart:refreshed}}));
      window.dispatchEvent(new CustomEvent('cart:updated',{detail:{cart:refreshed}}));
    }catch(e){}
  }

  async function syncSpringBarsCartState(r,row,variant){
    if(!r || !row || !variant) return;
    try{
      const root=(window.Shopify && Shopify.routes && Shopify.routes.root) ? Shopify.routes.root : '/';
      const cart=await fetch(root+'cart.js',{headers:{'Accept':'application/json'}}).then(x=>x.json());
      const item=(cart.items || []).find(x=>String(x.variant_id || x.id)===String(variant) && ownedSpringBars(x,r));
      if(item && Number(item.quantity)>0){
        const btn=row.querySelector('.daluca-ready-addon-row__action');
        const cents=Number(item.final_price || item.price || item.original_price || 0) || parseMoneyCents(r.dataset.dalucaSpringBarsPrice) || 500;
        markSpringBarsAdded(r,row,btn,cents);
      }
    }catch(e){}
  }

  function bindSpringBarsButton(row,variant,r,extraCents){
    if(!row || !variant) return;
    r=r || root();
    const btn=row.querySelector('.daluca-ready-addon-row__action');
    if(!btn) return;
    btn.disabled=false;
    btn.hidden=false;
    window.DaLucaI18n.setText(btn, 'Add +');
    btn.dataset.variantId=String(variant);

    if(btn.dataset.dalucaBound==='true') return;
    btn.dataset.dalucaBound='true';
    setTimeout(()=>syncSpringBarsCartState(r,row,variant),40);
    btn.addEventListener('click',async()=>{
      const id=Number(btn.dataset.variantId);
      if(!id || btn.disabled) return;

      if(row.classList.contains('is-added')){
        btn.disabled=true;
        window.DaLucaI18n.setText(btn, 'Removing…');
        try{
          await removeSpringBarsFromCart(r,row,btn,id);
        }catch(e){
          console.warn('[DaLuca] Spring bars add-on could not be removed:',e);
          btn.disabled=false;
          window.DaLucaI18n.setText(btn, 'Remove');
        }
        return;
      }

      btn.disabled=true; window.DaLucaI18n.setText(btn, 'Adding…');
      try{
        const root=(window.Shopify && Shopify.routes && Shopify.routes.root) ? Shopify.routes.root : '/';
        const res=await fetch(root+'cart/add.js',{
          method:'POST',
          headers:{'Content-Type':'application/json','Accept':'application/json'},
          body:JSON.stringify({items:[{id,quantity:1,properties:{'_DaLuca add-on':'Extra Standard Spring Bars','_DaLuca add-on for':springBarOwner(r)}}]})
        });

        let payload=null;
        try{ payload=await res.json(); }catch(e){}
        if(!res.ok){
          const message=(payload && (payload.description || payload.message)) || ('Cart add failed ('+res.status+')');
          const err=new Error(message);
          err.status=res.status;
          throw err;
        }

        const actualExtra=(payload && Array.isArray(payload.items) && payload.items[0])
          ? Number(payload.items[0].final_price || payload.items[0].price || payload.items[0].original_price || 0)
          : (Number(extraCents) || parseMoneyCents(r && r.dataset.dalucaSpringBarsPrice) || 500);
        markSpringBarsAdded(r,row,btn,actualExtra);
        try{
          const cart=await fetch(root+'cart.js',{headers:{'Accept':'application/json'}}).then(x=>x.json());
          document.dispatchEvent(new CustomEvent('cart:updated',{bubbles:true,detail:{cart}}));
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh',{bubbles:true,detail:{cart}}));
          window.dispatchEvent(new CustomEvent('cart:updated',{detail:{cart}}));
        }catch(e){}
      }catch(e){
        console.warn('[DaLuca] Spring bars add-on could not be added:',e);
        row.classList.add('is-add-error');
        btn.disabled=false;
        const msg=String((e && e.message) || '').toLowerCase();
        if(msg.includes('sold out') || msg.includes('cannot find variant') || msg.includes('available quantity')){
          row.classList.add('is-unavailable');
          window.DaLucaI18n.setText(btn, 'Unavailable');
          btn.disabled=true;
        }else{
          window.DaLucaI18n.setText(btn, 'Try again');
        }
      }
    });
  }

  async function resolveSpringBarsFallback(r,row){
    if(!r || !row) return;
    const btn=row.querySelector('.daluca-ready-addon-row__action');
    if(btn){ btn.disabled=true; window.DaLucaI18n.setText(btn, 'Loading…'); }

    // Default helper handle used by the Product information setting.
    const handle=clean(r.dataset.dalucaSpringBarsHandle) || 'extra-standard-spring-bars';
    try{
      const root=(window.Shopify && Shopify.routes && Shopify.routes.root) ? Shopify.routes.root : '/';
      const res=await fetch(root+'products/'+encodeURIComponent(handle)+'.js',{headers:{'Accept':'application/json'}});
      if(!res.ok) throw new Error('helper product missing');
      const product=await res.json();
      const variants=Array.isArray(product.variants) ? product.variants : [];
      // Only bind a variant Shopify says is actually available. The previous
      // fallback to variants[0] could bind a sold-out/unpublished variant and
      // then surface the generic "Try again" state after cart/add rejected it.
      const v=variants.find(x=>x && x.available===true);
      if(!v || !v.id) throw new Error('helper variant unavailable');

      r.dataset.dalucaSpringBarsVariantId=String(v.id);
      r.dataset.dalucaSpringBarsTitle=product.title || 'Extra Standard Spring Bars';
      r.dataset.dalucaSpringBarsPrice=moneyFromCents(v.price ?? product.price ?? 500);
      r.dataset.dalucaSpringBarsPriceCents=String(Number(v.price ?? product.price ?? 500) || 500);

      const title=row.querySelector('.daluca-ready-addon-row__copy strong');
      if(title) window.DaLucaI18n.setText(title, (product.title || 'Extra Standard Spring Bars')+' · '+moneyFromCents(v.price ?? product.price ?? 500));
      bindSpringBarsButton(row,v.id,r,Number(v.price ?? product.price ?? 500) || 500);
      row.classList.remove('is-unconfigured');
    }catch(e){
      // Do not expose theme configuration instructions to shoppers.
      row.classList.add('is-unconfigured');
      if(btn) btn.hidden=true;
    }
  }

  function springBars(r,b){
    if(!b || b.querySelector('.daluca-ready-spring-bars')) return;
    const variant=clean(r.dataset.dalucaSpringBarsVariantId);
    const title=clean(r.dataset.dalucaSpringBarsTitle) || 'Extra Standard Spring Bars';
    const price=clean(r.dataset.dalucaSpringBarsPrice) || '$5.00';

    const row=document.createElement('div');
    row.className='daluca-ready-addon-row daluca-ready-spring-bars';
    window.DaLucaI18n.setHtml(row, '<span class="daluca-ready-addon-row__check"></span><span class="daluca-ready-addon-row__copy"><strong>'+title+' · '+price+'</strong><small>This strap does not include spring bars; use your watch\'s current bars. Add one extra pair (1 set / 2 bars) for $5.</small></span><button class="daluca-ready-addon-row__action" type="button">Add +</button>');

    const intro=b.querySelector('.daluca-ready-setup-intro');
    if(intro) intro.insertAdjacentElement('afterend',row); else b.appendChild(row);

    if(variant){
      bindSpringBarsButton(row,variant,r,Number(r.dataset.dalucaSpringBarsPriceCents) || parseMoneyCents(price) || 500);
    }else{
      resolveSpringBarsFallback(r,row);
    }
  }

  function quickWarning(r,b,essentials){
    // V3.68: return-policy warning removed from the PDP add-on UI.
    // Custom products already carry their own made-to-order sales terms,
    // and this warning was redundant/noisy in the shared strap flow.
    return;
  }

  function directChildContaining(parent,node){
    if(!parent || !node) return null;
    let current=node;
    while(current && current.parentElement && current.parentElement!==parent){
      current=current.parentElement;
    }
    return current && current.parentElement===parent ? current : null;
  }

  function findCompactControl(root,pattern){
    const nodes=Array.from(root.querySelectorAll('label,strong,b,h1,h2,h3,h4,p,span,div'));
    const match=nodes.find(el=>{
      const t=clean(window.DaLucaI18n.sourceText(el));
      return t && pattern.test(t) && t.length<160;
    });
    if(!match) return null;

    let current=match;
    let best=null;
    for(let i=0; current && current!==root && i<6; i++,current=current.parentElement){
      const text=clean(window.DaLucaI18n.sourceText(current));
      const hasControl=!!current.querySelector('input,button,select');
      if(hasControl && text.length<650) best=current;
      if(current.parentElement===root && best) break;
    }
    return best || match.parentElement;
  }

  function prepareEssentialsGrid(essentials){
    if(!essentials) return;
    essentials.classList.add('daluca-ready-essentials-grid');

    // Cart/add state changes can cause the theme's product-card component to
    // replace or reflow individual accessory cards. Keep the three products
    // inside their own dedicated row so those internal state changes can never
    // change the outer Box 03 layout.
    let row=essentials.querySelector(':scope > .daluca-ready-essential-cards-row');
    if(!row){
      row=document.createElement('div');
      row.className='daluca-ready-essential-cards-row';
    }

    const allCards=Array.from(essentials.querySelectorAll('product-card.daluca-pdp-upsell, .daluca-pdp-upsell'))
      .filter((card,index,arr)=>arr.indexOf(card)===index)
      .filter(card=>!card.closest('.daluca-ready-essential-cards-row') || card.closest('.daluca-ready-essential-cards-row')===row);

    const cards=allCards.slice(0,3);
    cards.forEach(card=>card.classList.add('daluca-ready-essential-card'));

    if(cards.length){
      const firstTop=cards[0].closest('.daluca-ready-essential-cards-row') ? row : directChildContaining(essentials,cards[0]);
      if(!row.isConnected){
        const addAllExisting=essentials.querySelector(':scope > .daluca-ready-add-all-wrap');
        essentials.insertBefore(row, addAllExisting || firstTop || null);
      }
      cards.forEach(card=>{
        if(card.parentElement!==row) row.appendChild(card);
      });
    }

    // Mark the top-level child containing the "Add all three" control so it
    // stays below the dedicated 3-card row at full width.
    const allNodes=Array.from(essentials.querySelectorAll('button,a,div,span,strong'));
    const addAll=allNodes.find(el=>/^add all three\b/i.test(clean(window.DaLucaI18n.sourceText(el))));
    if(addAll){
      let direct=directChildContaining(essentials,addAll);
      if(!direct && addAll.closest('.daluca-ready-add-all-wrap')) direct=addAll.closest('.daluca-ready-add-all-wrap');
      if(direct){
        direct.classList.add('daluca-ready-add-all-wrap');
        if(row.isConnected && direct.previousElementSibling!==row){
          essentials.insertBefore(direct,row.nextSibling);
        }
      }
      addAll.classList.add('daluca-ready-add-all-control');
    }

    // Hide redundant explanatory copy above the three products while keeping
    // the actual Care & Installation heading.
    Array.from(essentials.querySelectorAll('p,small')).forEach(el=>{
      const t=clean(window.DaLucaI18n.sourceText(el));
      if(
        /useful additions|condition and protect|apply cream and finish|for clean, confident strap changes/i.test(t)
      ){
        el.classList.add('daluca-ready-compact-hide');
      }
    });

    // The product-card custom element can rerender itself after an item is
    // added to cart. Re-normalize the row after any child replacement without
    // allowing the observer to recursively thrash the DOM.
    if(!essentials.__dalucaEssentialObserver){
      let scheduled=false;
      const obs=new MutationObserver(()=>{
        if(scheduled) return;
        scheduled=true;
        requestAnimationFrame(()=>{
          scheduled=false;
          try{
            const freshCards=Array.from(essentials.querySelectorAll('product-card.daluca-pdp-upsell, .daluca-pdp-upsell'))
              .filter((card,index,arr)=>arr.indexOf(card)===index)
              .slice(0,3);
            freshCards.forEach(card=>card.classList.add('daluca-ready-essential-card'));
            let stableRow=essentials.querySelector(':scope > .daluca-ready-essential-cards-row');
            if(!stableRow){
              stableRow=document.createElement('div');
              stableRow.className='daluca-ready-essential-cards-row';
              const addWrap=essentials.querySelector(':scope > .daluca-ready-add-all-wrap');
              essentials.insertBefore(stableRow,addWrap || null);
            }
            freshCards.forEach(card=>{
              if(card.parentElement!==stableRow) stableRow.appendChild(card);
            });
            const addAllNow=Array.from(essentials.querySelectorAll('button,a,div,span,strong'))
              .find(el=>/^add all three\b/i.test(clean(window.DaLucaI18n.sourceText(el))));
            if(addAllNow){
              const wrap=directChildContaining(essentials,addAllNow) || addAllNow.closest('.daluca-ready-add-all-wrap');
              if(wrap){
                wrap.classList.add('daluca-ready-add-all-wrap');
                if(wrap.previousElementSibling!==stableRow) essentials.insertBefore(wrap,stableRow.nextSibling);
              }
              addAllNow.classList.add('daluca-ready-add-all-control');
            }
          }catch(e){}
        });
      });
      obs.observe(essentials,{childList:true,subtree:true});
      essentials.__dalucaEssentialObserver=obs;
    }
  }

  function isVintageReady(r){
    if(!r) return false;
    return r.dataset.dalucaVintage==='true' || r.classList.contains('daluca-pdp--vintage');
  }


  function findVintageInlineSpec(r,key){
    if(!r) return null;
    const escaped=key.replace(/[.*+?^${}()|[\]\\]/g,'\\$&');
    const re=new RegExp('^\\s*'+escaped+'\\s*:?\\s*(.+?)\\s*$','i');
    const other=['Length','Width','Thickness','Buckle','Material']
      .filter(k=>k.toLowerCase()!==key.toLowerCase());

    const candidates=Array.from(r.querySelectorAll('li,dt,dd,div,p,span'))
      .filter(el=>{
        if(el.closest('.daluca-ready-story-specs__specs')) return false;
        const t=clean(window.DaLucaI18n.productSourceText(el));
        if(!t || t.length>220) return false;
        const m=t.match(re);
        if(!m || !clean(m[1])) return false;
        const value=clean(m[1]);
        const lower=value.toLowerCase();
        if(other.some(k=>lower.includes(k.toLowerCase()))) return false;
        return true;
      })
      .sort((a,b)=>{
        const ac=a.children.length, bc=b.children.length;
        if(ac!==bc) return ac-bc;
        return clean(window.DaLucaI18n.productSourceText(a)).length-clean(window.DaLucaI18n.productSourceText(b)).length;
      });

    for(const el of candidates){
      const t=clean(window.DaLucaI18n.productSourceText(el));
      const m=t.match(re);
      if(!m) continue;
      const value=clean(m[1]).replace(/^[:\-]\s*/,'');
      if(!value || value.length>180) continue;

      // Prefer the smallest row/item that contains this single specification.
      let source=el;
      if(el.tagName==='SPAN' && el.parentElement){
        const pt=clean(window.DaLucaI18n.productSourceText(el.parentElement));
        const pm=pt.match(re);
        if(pm && clean(pm[1])===value && pt.length<220) source=el.parentElement;
      }

      return {value:window.DaLucaI18n.locale !== 'en' ? (window.DaLucaI18n.productField(el)?.value || value) : value,source};
    }
    return null;
  }

  function repairVintageInlineSpecs(r){
    if(!r || !isVintageReady(r)) return false;

    const keys=['Length','Width','Thickness','Material','Hardware','Buckle','Features'];
    const found={};
    keys.forEach(key=>{
      const hit=findVintageInlineSpec(r,key);
      if(hit) found[key]=hit;
    });

    // Only perform the repair when the real vintage inline specs are present.
    if(Object.keys(found).length<4) return false;

    let grid=r.querySelector('.daluca-ready-story-specs');

    // Emergency vintage fallback: the Story can be outside the inner info
    // wrapper. Build the editorial Story/Specs row from the full root.
    if(!grid){
      const infoWrap=r.querySelector(':scope > .product-information') || r.querySelector('.product-information');
      const info=infoWrap && (infoWrap.querySelector('.product-information--inner') || infoWrap);
      const holder=r.querySelector('.daluca-ready-steps');

      let story=(info && findStoryNode(info)) || findStoryNode(r);
      if(story){
        ensureVintageStoryHeading(story);
        grid=document.createElement('section');
        grid.className='daluca-ready-story-specs daluca-ready-story-specs--vintage';

        const left=document.createElement('div');
        left.className='daluca-ready-story-specs__story';
        left.appendChild(story);

        const specsFallback=document.createElement('div');
        specsFallback.className='daluca-ready-story-specs__specs';

        grid.append(left,specsFallback);
        r.insertBefore(grid,holder || null);
        r.dataset.dalucaVintageStoryGrid='true';
      }
    }

    if(!grid) return false;

    let specs=grid.querySelector('.daluca-ready-story-specs__specs');
    if(!specs){
      specs=document.createElement('div');
      specs.className='daluca-ready-story-specs__specs';
      grid.appendChild(specs);
    }

    window.DaLucaI18n.setHtml(specs, '<span class="daluca-ready-story-specs__eyebrow">Specifications</span>');

    const dl=document.createElement('dl');
    dl.className='daluca-ready-specs-list';

    keys.forEach(key=>{
      const hit=found[key];
      if(!hit) return;

      const row=document.createElement('div');
      row.className='daluca-ready-specs-row';
      window.DaLucaI18n.setHtml(row, '<dt>'+key+'</dt><dd>'+hit.value+'</dd>');
      dl.appendChild(row);

      if(hit.source){
        hit.source.classList.add('daluca-ready-vintage-inline-spec-source');
      }
    });

    specs.appendChild(dl);

    const note=document.createElement('p');
    note.className='daluca-ready-specs-note';
    window.DaLucaI18n.setText(note, 'You receive the exact strap pictured.');
    specs.appendChild(note);

    // Hide any original exact-strap note left behind in the old vintage content.
    Array.from(r.querySelectorAll('p,em,i,small,div,li')).forEach(el=>{
      if(
        !el.closest('.daluca-ready-story-specs__specs') &&
        /^you receive the exact strap pictured\.?$/i.test(clean(window.DaLucaI18n.sourceText(el)))
      ){
        el.classList.add('daluca-ready-source-note');
      }
    });

    r.dataset.dalucaVintageSpecsRepaired='true';
    return true;
  }


  function removeVintageSetupStrayPluses(setup){
    if(!setup) return;

    // The $5 spring-bar row does not need a decorative leading + on vintage.
    // Remove the node itself rather than relying on CSS so theme rules cannot
    // make it reappear.
    setup.querySelectorAll('.daluca-ready-spring-bars .daluca-ready-addon-row__check, .daluca-ready-vintage-spring-row .daluca-ready-addon-row__check').forEach(el=>el.remove());

    // Defensive cleanup for a standalone + element inserted by older markup.
    Array.from(setup.querySelectorAll('span,div,i,b,strong')).forEach(el=>{
      if(clean(window.DaLucaI18n.sourceText(el))==='+' && !el.closest('button,a')) el.remove();
    });

    // Defensive cleanup for a raw standalone + text node. This intentionally
    // does NOT touch "Add +" because that text is not exactly "+".
    const walker=document.createTreeWalker(setup,NodeFilter.SHOW_TEXT);
    const remove=[];
    let node;
    while((node=walker.nextNode())){
      if(String(node.nodeValue || '').trim()==='+') remove.push(node);
    }
    remove.forEach(node=>node.remove());

    // Some theme/product-card markup can include literal slash separators.
    // Remove only standalone separator tokens inside the three vintage care cards.
    setup.querySelectorAll('product-card.daluca-ready-essential-card').forEach(card=>{
      Array.from(card.querySelectorAll('span,div,i,b,strong,small')).forEach(el=>{
        const t=clean(window.DaLucaI18n.sourceText(el));
        if(/^(?:\/|\\|\|\/?\s*\/?)$/.test(t) && !el.closest('button,a')) el.remove();
      });

      const slashWalker=document.createTreeWalker(card,NodeFilter.SHOW_TEXT);
      const slashRemove=[];
      let slashNode;
      while((slashNode=slashWalker.nextNode())){
        const t=String(slashNode.nodeValue || '').trim();
        if(t==='/' || t==='\\' || t==='|' || t==='(/)' || t==='( / )') slashRemove.push(slashNode);
      }
      slashRemove.forEach(node=>node.remove());
    });
  }

  function applyVintageReadyVariant(r){
    if(!isVintageReady(r)) return;
    r.classList.add('daluca-ready--vintage');

    // Make the Story eyebrow explicitly match a vintage ready-made strap.
    Array.from(r.querySelectorAll('span,p,div,h1,h2,h3,h4,h5,h6')).forEach(el=>{
      if(clean(window.DaLucaI18n.sourceText(el)).toLowerCase()==='the story of this strap'){
        window.DaLucaI18n.setText(el, 'The story of this vintage strap');
      }
    });

    const setup=r.querySelector('.daluca-ready-step--setup');
    if(setup){
      const intro=setup.querySelector('.daluca-ready-setup-intro');
      if(intro) window.DaLucaI18n.setText(intro, 'Useful installation and care extras for your vintage strap.');

      // Vintage finished straps are never offered with the Quick Release modification.
      const quick=findCompactControl(setup,/^quick[-\\s]?release spring bars$/i);
      if(quick) quick.remove();
      setup.classList.add('daluca-ready-step--vintage');
      removeVintageSetupStrayPluses(setup);
    }
  }

  function compactSetupBox(r,b,essentials){
    if(!b || b.dataset.dalucaSetupCompacted==='true') return;

    const vintage=isVintageReady(r);
    if(vintage){
      const vintageQuick=findCompactControl(b,/^quick[-\s]?release spring bars$/i);
      if(vintageQuick) vintageQuick.remove();
      b.closest('.daluca-ready-step--setup')?.classList.add('daluca-ready-step--vintage');
    }

    // Remove the redundant internal "Optional upgrade" heading.
    Array.from(b.querySelectorAll('h1,h2,h3,h4,strong,p,div')).forEach(el=>{
      if(clean(window.DaLucaI18n.sourceText(el)).toLowerCase()==='optional upgrade'){
        el.classList.add('daluca-ready-optional-heading-hidden');
      }
    });

    // Hide the implementation/cart-mechanics note. The important
    // non-returnable warning remains visible in red.
    Array.from(b.querySelectorAll('p,small,div,span')).forEach(el=>{
      const t=clean(window.DaLucaI18n.sourceText(el));
      if(/^added as a separate, correctly priced item in your cart\.?$/i.test(t)){
        el.classList.add('daluca-ready-compact-hide');
      }
    });

    const spring=b.querySelector('.daluca-ready-spring-bars');
    const quick=vintage ? null : findCompactControl(b,/^quick[-\s]?release spring bars$/i);

    if(vintage && spring){
      spring.classList.add('daluca-ready-vintage-spring-row');
    }

    if(spring && quick && quick!==spring && !b.querySelector('.daluca-ready-config-grid')){
      const grid=document.createElement('div');
      grid.className='daluca-ready-config-grid';

      const intro=b.querySelector('.daluca-ready-setup-intro');
      if(intro) intro.insertAdjacentElement('afterend',grid);
      else b.insertBefore(grid,b.firstChild);

      spring.classList.add('daluca-ready-config-card','daluca-ready-config-card--spring');
      quick.classList.add('daluca-ready-config-card','daluca-ready-config-card--quick');

      grid.appendChild(spring);
      grid.appendChild(quick);

      const warning=b.querySelector('.daluca-ready-quick-return-warning');
      if(warning){
        warning.classList.add('daluca-ready-config-warning');
        quick.appendChild(warning);
      }
    }

    prepareEssentialsGrid(essentials);

    // Product-card markup can include standalone slash/separator tokens.
    // They are not meaningful UI and can appear underneath the price on the
    // non-vintage care cards. Remove only standalone separator tokens so
    // legitimate product text is untouched.
    if(essentials){
      essentials.querySelectorAll('product-card.daluca-ready-essential-card').forEach(card=>{
        Array.from(card.querySelectorAll('span,div,i,b,strong,small')).forEach(el=>{
          const t=clean(window.DaLucaI18n.sourceText(el));
          if((t==='/' || t==='\\' || t==='|' || t==='(/)' || t==='( / )') && !el.closest('button,a')){
            el.remove();
          }
        });

        const walker=document.createTreeWalker(card,NodeFilter.SHOW_TEXT);
        const remove=[];
        let node;
        while((node=walker.nextNode())){
          const t=String(node.nodeValue || '').trim();
          if(t==='/' || t==='\\' || t==='|' || t==='(/)' || t==='( / )'){
            remove.push(node);
          }
        }
        remove.forEach(node=>node.remove());
      });
    }

    if(vintage){
      const setup=b.closest('.daluca-ready-step--setup') || b;
      removeVintageSetupStrayPluses(setup);
    }
    b.dataset.dalucaSetupCompacted='true';
  }

  function setupBox(r,step,essentials){
    if(!step) return;
    setTitle(step,'Complete the Setup');
    step.classList.add('daluca-ready-step--setup');

    // V3.67: the server-rendered master add-on section IS Step 03.
    // Do not inject the retired spring-bar row / legacy essentials into it.
    if(step.classList.contains('daluca-master-addons-v366')){
      step.classList.add('daluca-ready-step--master-addons');
      return;
    }

    const b=body(step); if(!b) return;

    const generatedVintage=step.hasAttribute('data-daluca-generated-vintage-setup');

    if(!generatedVintage && !b.querySelector('.daluca-ready-setup-title')){
      const h=document.createElement('h3');
      h.className='daluca-ready-setup-title';
      window.DaLucaI18n.setText(h, 'Complete the Setup');
      b.insertBefore(h,b.firstChild);
    }

    if(!b.querySelector('.daluca-ready-setup-intro')){
      const p=document.createElement('p');
      p.className='daluca-ready-setup-intro';
      window.DaLucaI18n.setText(p, 'Useful extras and eligible strap modifications, all in one place.');
      const title=b.querySelector('.daluca-ready-setup-title');
      if(title) title.insertAdjacentElement('afterend',p); else b.insertBefore(p,b.firstChild);
    }

    springBars(r,b);
    if(essentials) b.appendChild(essentials);
    quickWarning(r,b,essentials);
    compactSetupBox(r,b,essentials);
  }

  function makeGeneratedVintageSetupStep(){
    const step=document.createElement('section');
    step.className='daluca-concierge-step daluca-ready-generated-vintage-setup';
    step.setAttribute('data-daluca-generated-vintage-setup','true');

    const head=document.createElement('div');
    head.className='daluca-concierge-step__head';
    window.DaLucaI18n.setHtml(head, '<span class="daluca-ready-generated-step-number">03</span><h3>Complete the Setup</h3>');

    const b=document.createElement('div');
    b.className='daluca-concierge-step__body';

    step.append(head,b);

    const generatedNumber=head.querySelector('.daluca-ready-generated-step-number');
    if(generatedNumber){
      step.insertBefore(generatedNumber,head);
    }

    return step;
  }

  function fitSearchInput(step){
    if(!step) return null;
    const inputs=Array.from(step.querySelectorAll('input[type="search"],input[type="text"],input:not([type])'));
    return inputs.find(i=>/search|watch|model|reference|rolex/i.test(String(i.placeholder || ''))) || inputs[0] || null;
  }

  function forceFitSearchVisible(step){
    if(!step) return null;

    // The finder hides its search UI after a selection by toggling hidden /
    // aria-hidden / display on one or more wrappers.  Capture the original
    // visible display modes before that happens, then restore them explicitly.
    // This keeps the search box available after BOTH a correct and incorrect
    // watch selection without replacing the finder input or its listeners.
    let input=fitSearchInput(step);

    // If the finder temporarily detached its input, reuse the original node
    // rather than creating a dead clone with no search behavior.
    if(!input && step.__dalucaFitSearchInput){
      const saved=step.__dalucaFitSearchInput;
      const shell=step.__dalucaFitSearchShell;
      const target=body(step) || step;
      try{
        if(shell && !shell.isConnected){
          target.insertBefore(shell,target.firstChild || null);
        }else if(!saved.isConnected){
          target.insertBefore(saved,target.firstChild || null);
        }
      }catch(e){}
      input=fitSearchInput(step) || saved;
    }

    if(!input) return null;

    if(!step.__dalucaFitSearchInput){
      step.__dalucaFitSearchInput=input;
      step.__dalucaFitSearchShell=input.parentElement && input.parentElement!==step ? input.parentElement : null;
    }

    function restoreNode(el,fallback){
      if(!el) return;
      try{
        const computed=getComputedStyle(el);
        if(!el.dataset.dalucaFitRestoreDisplay && computed.display && computed.display!=='none'){
          el.dataset.dalucaFitRestoreDisplay=computed.display;
        }
        const display=(el.dataset.dalucaFitRestoreDisplay && el.dataset.dalucaFitRestoreDisplay!=='none')
          ? el.dataset.dalucaFitRestoreDisplay
          : fallback;
        el.hidden=false;
        el.removeAttribute('hidden');
        el.setAttribute('aria-hidden','false');
        el.style.setProperty('display',display,'important');
        el.style.setProperty('visibility','visible','important');
        el.style.setProperty('opacity','1','important');
        el.style.setProperty('max-height','none','important');
      }catch(e){}
    }

    input.classList.add('daluca-ready-fit-search-input');
    restoreNode(input,'block');

    // Walk all the way back to the step.  The finder has used different
    // wrapper depths in different result states, so four ancestors was not
    // enough in every mismatch case.
    let current=input.parentElement;
    for(let i=0; current && current!==step && i<10; i++,current=current.parentElement){
      current.classList.add('daluca-ready-fit-search-visible');
      restoreNode(current,'block');
    }

    return input;
  }

  function resetReadyFitSelection(step,watchName,watchKey){
    if(!step) return;
    step.dataset.dalucaSuppressedWatch=watchKey || '';
    step.classList.add('daluca-ready-fit-cleared');
    clearSavedWatchStorage(watchName || '');

    const card=step.querySelector('.daluca-ready-fit-match-card');
    if(card) card.remove();
    step.querySelectorAll('.daluca-ready-fit-warning').forEach(el=>el.classList.add('daluca-ready-fit-warning--hidden'));

    const input=forceFitSearchVisible(step);
    if(input){
      input.value='';
      ['input','change','keyup'].forEach(type=>input.dispatchEvent(new Event(type,{bubbles:true})));
      setTimeout(()=>{
        try{
          forceFitSearchVisible(step);
          input.focus();
        }catch(e){}
      },30);
    }
  }

  function clearSavedWatchStorage(watchName){
    const needle=clean(watchName).toLowerCase();
    if(!needle) return;
    [window.localStorage,window.sessionStorage].forEach(store=>{
      try{
        for(let i=store.length-1;i>=0;i--){
          const key=store.key(i);
          if(!key || !/(daluca|watch|strap|finder|fit)/i.test(key)) continue;
          const value=String(store.getItem(key) || '').toLowerCase();
          if(value.includes(needle)) store.removeItem(key);
        }
      }catch(e){}
    });
  }

  function makeFitMatchCard(step,source,watchName,watchWidth){
    let card=step.querySelector('.daluca-ready-fit-match-card');
    if(!card){
      card=document.createElement('div');
      card.className='daluca-ready-fit-match daluca-ready-fit-match-card';
      source.insertAdjacentElement('afterend',card);
    }
    card.dataset.watchKey=(watchName+'|'+watchWidth).toLowerCase();
    window.DaLucaI18n.setHtml(card, '<div class="daluca-ready-fit-match__top"><span class="daluca-ready-fit-match__label">Matched watch</span><button class="daluca-ready-fit-change" type="button" aria-label="Change selected watch">Change</button></div><span class="daluca-ready-fit-match__main"><strong>'+watchName+'</strong>'+(watchWidth?'<em>'+watchWidth+'</em>':'')+'</span>');
    return card;
  }


  function centerReadyFitStep(step){
    if(!step) return null;

    let group=step.querySelector(':scope > .daluca-ready-fit-center-group');
    if(group) return group;

    const head=step.querySelector(':scope > .daluca-concierge-step__head') || step.querySelector('.daluca-concierge-step__head');
    const title=head && head.querySelector('h2,h3,h4');
    const b=body(step);
    const skip=step.querySelector(':scope > .daluca-concierge-step__skip') || step.querySelector('.daluca-concierge-step__skip');

    group=document.createElement('div');
    group.className='daluca-ready-fit-center-group';

    if(head && head.parentElement===step){
      head.insertAdjacentElement('afterend',group);
    }else{
      step.appendChild(group);
    }

    if(title) group.appendChild(title);
    if(b && b!==step) group.appendChild(b);

    if(skip && !group.contains(skip)){
      group.appendChild(skip);
    }

    return group;
  }


  function alignReadyFitSearchToWidthCard(step){
    if(!step || window.innerWidth<1181) return;

    const group=step.querySelector(':scope > .daluca-ready-fit-center-group');
    const input=fitSearchInput(step);
    const holder=step.parentElement;
    const widthCard=holder && holder.querySelector('.daluca-ready-width-card');

    if(!group || !input || !widthCard) return;

    group.style.bottom='auto';
    group.style.height='auto';

    const stepRect=step.getBoundingClientRect();
    const groupRect=group.getBoundingClientRect();
    const inputRect=input.getBoundingClientRect();
    const widthRect=widthCard.getBoundingClientRect();

    const inputOffset=inputRect.top-groupRect.top;
    let targetTop=(widthRect.top-stepRect.top)-inputOffset-8;

    const needed=Math.max(group.scrollHeight,groupRect.height);
    const maxTop=Math.max(54,step.clientHeight-needed-18);
    targetTop=Math.max(54,Math.min(targetTop,maxTop));

    group.style.top=Math.round(targetTop)+'px';
  }

  function scheduleReadyFitAlignment(step){
    if(!step) return;

    requestAnimationFrame(()=>{
      try{ alignReadyFitSearchToWidthCard(step); }catch(e){}
    });

    setTimeout(()=>{
      try{ alignReadyFitSearchToWidthCard(step); }catch(e){}
    },120);

    if(!step.dataset.dalucaFitResizeBound){
      step.dataset.dalucaFitResizeBound='true';
      window.addEventListener('resize',()=>{
        try{ alignReadyFitSearchToWidthCard(step); }catch(e){}
      },{passive:true});
    }
  }

  function polishReadyFitChecker(r,step){
    if(!r || !step) return;
    forceFitSearchVisible(step);
    step.querySelectorAll('.daluca-ready-fit-match-card').forEach(function(card){card.remove()});
    step.querySelectorAll('[data-daluca-pdp-fit-saved]').forEach(function(saved){
      saved.classList.remove('daluca-ready-fit-source');
    });
  }

  function steps(r,info){
    let holder=r.querySelector('.daluca-ready-steps');
    if(holder) return holder;
    if(r.dataset.dalucaProductAvailable==='false'){
      const unavailable=r.querySelector('[data-daluca-sold-out]');
      if(!unavailable) return null;
      holder=document.createElement('section');
      holder.className='daluca-ready-steps daluca-ready-steps--sold-out';
      holder.setAttribute('aria-label',unavailable.getAttribute('aria-label'));
      holder.appendChild(unavailable);
      r.appendChild(holder);
      return holder;
    }

    let list=Array.from(info.querySelectorAll(':scope > .daluca-concierge-step'));
    if(list.length<3){
      list=Array.from(info.querySelectorAll('.daluca-concierge-step')).filter((x,i,a)=>a.indexOf(x)===i);
    }

    if(list.length<3){
      list=Array.from(r.querySelectorAll('.daluca-concierge-step')).filter((x,i,a)=>a.indexOf(x)===i);
    }

    const vintage=isVintageReady(r);
    let four=[];

    if(vintage && list.length===3){
      // Vintage ready-made products intentionally start with only three
      // concierge steps: Confirm Watch, Width and Complete Order.
      // Generate the missing Setup step instead of aborting the entire build.
      const generated=makeGeneratedVintageSetupStep();
      four=[list[0],list[1],generated,list[2]];
      r.dataset.dalucaVintageGeneratedSetup='true';
    }else{
      if(list.length<4) return null;
      four=list.slice(0,4);
    }

    holder=document.createElement('section');
    holder.className='daluca-ready-steps';
    holder.setAttribute('aria-label','Ready-made strap ordering steps');
    if(vintage) holder.classList.add('daluca-ready-steps--vintage');

    four.forEach(s=>holder.appendChild(s));

    setTitle(four[0],'Confirm Your Watch');
    setStepNumber(four[0],1);
    polishReadyFitChecker(r,four[0]);
    makeWidthCard(r,four[1]);
    setStepNumber(four[1],2);
    setTitle(four[3],'Complete Your Order');
    setStepNumber(four[3],4);

    const essentials=info.querySelector('.daluca-pdp-upsells--essentials') || r.querySelector('.daluca-pdp-upsells--essentials') || document.querySelector('.daluca-pdp-upsells--essentials');
    setupBox(r,four[2],essentials);

    // Vintage setup deliberately has no Quick Release modification.
    if(vintage){
      const setup=four[2];
      setup.classList.add('daluca-ready-step--vintage');
      const quick=findCompactControl(setup,/^quick[-\\s]?release spring bars$/i);
      if(quick) quick.remove();
      const warning=setup.querySelector('.daluca-ready-quick-return-warning');
      if(warning) warning.remove();
      const intro=setup.querySelector('.daluca-ready-setup-intro');
      if(intro) window.DaLucaI18n.setText(intro, 'Useful installation and care extras for your vintage strap.');
    }

    r.appendChild(holder);
    return holder;
  }


  function dlTextEquals(el,text){
    return clean(el && window.DaLucaI18n.sourceText(el)).toLowerCase()===String(text).toLowerCase();
  }

  function dlFindHeading(text){
    return Array.from(document.querySelectorAll('h1,h2,h3,h4,h5,h6,p,span,div')).find(el=>{
      if(el.children.length>2) return false;
      return dlTextEquals(el,text);
    }) || null;
  }

  function dalucaThemeAssetUrl(filename){
    try{
      const script=Array.from(document.scripts).find(s=>/daluca-ready-pdp\.js/i.test(String(s.src||'')));
      if(script && script.src) return new URL(filename,script.src).href;
    }catch(e){}
    return filename;
  }

  function ensureDalucaLugWatchImage(card,label,fitHeading){
    if(!card) return null;

    let diagram=card.querySelector('.daluca-craft-journey__diagram');
    let img=card.querySelector('.daluca-ready-lug-watch-image');

    if(!img){
      img=document.createElement('img');
      img.className='daluca-ready-lug-watch-image';

      if(diagram){
        window.DaLucaI18n.setHtml(diagram, '');
        diagram.appendChild(img);
      }else if(label){
        label.insertAdjacentElement('beforebegin',img);
      }else if(fitHeading){
        fitHeading.insertAdjacentElement('beforebegin',img);
      }else{
        card.insertBefore(img,card.firstChild || null);
      }
    }

    img.src='https://cdn.shopify.com/s/files/1/0857/0362/2962/files/minimalist-watch-lug-width-diagram-clean-khaki.png';
    img.alt='';
    img.setAttribute('aria-hidden','true');
    img.loading='eager';
    img.decoding='async';
    img.style.display='block';
    img.onerror=null;

    return img;
  }


  function hardBalanceReadyWorkshop(section,fitCol,handCol,detailsCol){
    if(!section || window.innerWidth<768) return;

    const set=(el,prop,val)=>{
      if(el) el.style.setProperty(prop,val,'important');
    };

    /* Remove duplicate Lug Width labels across the ENTIRE left column.
       Keep only the one closest to the Fit heading. */
    if(fitCol){
      const fitHeading=dlFindHeading('Fit, without guessing');
      const exact=Array.from(fitCol.querySelectorAll('p,span,div,small')).filter(el=>
        /^lug\s*width$/i.test(clean(window.DaLucaI18n.sourceText(el)))
      );

      if(exact.length>1){
        let keep=exact[exact.length-1];

        if(fitHeading){
          const fhTop=fitHeading.getBoundingClientRect().top;
          const above=exact
            .map(el=>({el,top:el.getBoundingClientRect().top}))
            .filter(x=>x.top<=fhTop)
            .sort((a,b)=>b.top-a.top);
          if(above.length) keep=above[0].el;
        }

        exact.forEach(el=>{
          if(el!==keep) el.remove();
        });
      }
    }

    /* Kill the accumulated height/centering rules from prior iterations.
       The right image grid should define the natural minimum height. */
    [
      [section,'display','grid'],
      [section,'align-items','start'],
      [section,'padding-top','8px'],
      [section,'padding-bottom','8px'],
      [section,'margin-top','0'],
      [section,'margin-bottom','0'],
      [section,'min-height','0'],
      [section,'max-height','none'],
      [section,'height','auto'],
      [section,'overflow','hidden'],

      [fitCol,'display','flex'],
      [fitCol,'flex-direction','column'],
      [fitCol,'justify-content','flex-start'],
      [fitCol,'align-items','center'],
      [fitCol,'align-self','start'],
      [fitCol,'padding-top','10px'],
      [fitCol,'padding-bottom','4px'],
      [fitCol,'margin-top','0'],
      [fitCol,'margin-bottom','0'],
      [fitCol,'min-height','0'],
      [fitCol,'height','auto'],

      [handCol,'display','flex'],
      [handCol,'flex-direction','column'],
      [handCol,'justify-content','flex-start'],
      [handCol,'align-self','start'],
      [handCol,'padding-top','8px'],
      [handCol,'padding-bottom','4px'],
      [handCol,'margin-top','0'],
      [handCol,'margin-bottom','0'],
      [handCol,'min-height','0'],
      [handCol,'height','auto'],

      [detailsCol,'align-self','start'],
      [detailsCol,'padding-top','4px'],
      [detailsCol,'padding-bottom','4px'],
      [detailsCol,'margin-top','0'],
      [detailsCol,'margin-bottom','0'],
      [detailsCol,'min-height','0'],
      [detailsCol,'height','auto']
    ].forEach(([el,p,v])=>set(el,p,v));

    const outer=section.closest('.daluca-ready-workshop-section');
    if(outer){
      set(outer,'padding-top','0');
      set(outer,'padding-bottom','0');
      set(outer,'margin-top','0');
      set(outer,'margin-bottom','0');
      set(outer,'min-height','0');
      set(outer,'max-height','none');
      set(outer,'height','auto');
      set(outer,'overflow','hidden');
    }

    /* Tighten the left composition itself. */
    if(fitCol){
      const img=fitCol.querySelector('.daluca-ready-lug-watch-image');
      const label=fitCol.querySelector('.daluca-ready-lug-width-label');
      const fitHeading=dlFindHeading('Fit, without guessing');

      if(img){
        set(img,'margin-top','0');
        set(img,'margin-bottom','4px');
      }
      if(label){
        set(label,'margin-top','0');
        set(label,'margin-bottom','6px');
      }
      if(fitHeading){
        set(fitHeading,'margin-top','0');
        set(fitHeading,'margin-bottom','8px');
      }
    }

    /* Middle panel title/photo/copy should start near the top rather than
       floating vertically in the oversized column. */
    if(handCol){
      const handHeading=dlFindHeading('Made by hand, one strap at a time');
      if(handHeading){
        set(handHeading,'margin-top','0');
        set(handHeading,'margin-bottom','8px');
      }
      const img=handCol.querySelector('img');
      if(img){
        set(img,'margin-top','6px');
        set(img,'margin-bottom','8px');
      }
    }

    /* After the new top alignment is painted, calculate the lowest REAL
       content edge and make the khaki box end just beneath it. */
    requestAnimationFrame(()=>{
      try{
        const bandTop=section.getBoundingClientRect().top;
        let maxBottom=bandTop;

        [fitCol,handCol,detailsCol].forEach(col=>{
          if(!col) return;
          const nodes=[col,...Array.from(col.querySelectorAll(':scope > *'))];
          nodes.forEach(el=>{
            const cs=getComputedStyle(el);
            if(cs.display==='none' || cs.visibility==='hidden') return;
            const r=el.getBoundingClientRect();
            if(r.height>0) maxBottom=Math.max(maxBottom,r.bottom);
          });
        });

        const target=Math.max(0,Math.ceil(maxBottom-bandTop+10));
        if(target>0){
          set(section,'height',target+'px');
          set(section,'max-height',target+'px');
          set(section,'min-height',target+'px');
        }

        if(outer){
          set(outer,'height','auto');
          set(outer,'min-height','0');
          set(outer,'max-height','none');
        }
      }catch(e){}
    });
  }


  function dalucaColorParts(value){
    const m=String(value||'').match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*([0-9.]+))?\s*\)/i);
    if(!m) return null;
    return [Number(m[1]),Number(m[2]),Number(m[3]),m[4]==null?1:Number(m[4])];
  }

  function dalucaSameColor(a,b){
    if(!a || !b) return false;
    return Math.abs(a[0]-b[0])<4 && Math.abs(a[1]-b[1])<4 && Math.abs(a[2]-b[2])<4;
  }

  function findFullWorkshopShell(section){
    if(!section) return null;

    const chain=[];
    let node=section;
    for(let i=0; node && node!==document.body && i<10; i++,node=node.parentElement){
      chain.push(node);
    }

    let baseColor=null;
    for(const el of chain){
      const c=dalucaColorParts(getComputedStyle(el).backgroundColor);
      if(c && c[3]>.05){
        baseColor=c;
        break;
      }
    }

    let best=section;
    let bestWidth=section.getBoundingClientRect().width;

    for(const el of chain){
      const rect=el.getBoundingClientRect();
      const c=dalucaColorParts(getComputedStyle(el).backgroundColor);

      if(
        rect.width>=bestWidth &&
        (
          (baseColor && dalucaSameColor(c,baseColor)) ||
          rect.width>=document.documentElement.clientWidth*.94
        )
      ){
        best=el;
        bestWidth=rect.width;
      }
    }

    return best;
  }

  function finalWorkshopGeometry(section,fitCol,handCol,detailsCol){
    if(!section || window.innerWidth<768) return;

    const set=(el,prop,val)=>{
      if(el) el.style.setProperty(prop,val,'important');
    };

    const shell=findFullWorkshopShell(section);
    if(!shell) return;

    shell.classList.add('daluca-ready-workshop-shell');
    section.classList.add('daluca-ready-workshop-inner');

    /* The old visible border lived on the centered inner container, which is
       why it stopped short of the viewport edges. The new full-width shell
       owns the top/bottom lines instead. */
    set(shell,'position','relative');
    set(shell,'box-sizing','border-box');
    set(shell,'padding-top','0');
    set(shell,'padding-bottom','0');
    set(shell,'margin-top','0');
    set(shell,'margin-bottom','0');
    set(shell,'min-height','0');
    set(shell,'max-height','none');
    set(shell,'overflow','visible');

    set(section,'border-top','0');
    set(section,'border-bottom','0');
    set(section,'box-shadow','none');
    set(section,'margin-top','0');
    set(section,'margin-bottom','0');
    set(section,'padding-top','18px');
    set(section,'padding-bottom','0');
    set(section,'min-height','0');
    set(section,'height','auto');
    set(section,'max-height','none');
    set(section,'align-items','start');
    set(section,'overflow','visible');

    [fitCol,handCol,detailsCol].forEach(col=>{
      if(!col) return;
      set(col,'align-self','start');
      set(col,'justify-content','flex-start');
      set(col,'min-height','0');
      set(col,'height','auto');
      set(col,'max-height','none');
      set(col,'margin-top','0');
      set(col,'margin-bottom','0');
      set(col,'padding-top','0');
      set(col,'padding-bottom','0');
    });

    /* Normalize the left panel DOM itself. Previous theme markup contains
       more than one "Lug Width" node on some products. Keep exactly one. */
    if(fitCol){
      const fitHeading=dlFindHeading('Fit, without guessing');
      const leaves=Array.from(fitCol.querySelectorAll('*')).filter(el=>
        el.children.length===0 && /^lug\s*width$/i.test(clean(window.DaLucaI18n.sourceText(el)))
      );
      leaves.forEach(el=>el.remove());

      let label=fitCol.querySelector('.daluca-ready-lug-width-label');
      if(!label){
        label=document.createElement('div');
        label.className='daluca-ready-lug-width-label';
        window.DaLucaI18n.setText(label, 'Lug Width');
      }else{
        window.DaLucaI18n.setText(label, 'Lug Width');
      }

      if(fitHeading) fitHeading.insertAdjacentElement('beforebegin',label);

      const measured=Array.from(fitCol.querySelectorAll('*')).find(el=>
        el.children.length===0 && /^measured\s+for\s+your\s+watch$/i.test(clean(window.DaLucaI18n.sourceText(el)))
      );
      const img=fitCol.querySelector('.daluca-ready-lug-watch-image');

      if(img && measured){
        measured.insertAdjacentElement('beforebegin',img);
      }

      set(fitCol,'display','flex');
      set(fitCol,'flex-direction','column');
      set(fitCol,'align-items','center');
      set(fitCol,'text-align','center');

      if(img){
        set(img,'margin-top','0');
        set(img,'margin-bottom','5px');
      }
      if(measured){
        set(measured,'margin-top','0');
        set(measured,'margin-bottom','5px');
      }
      if(label){
        set(label,'margin-top','0');
        set(label,'margin-bottom','6px');
      }
      if(fitHeading){
        set(fitHeading,'margin-top','0');
        set(fitHeading,'margin-bottom','8px');
      }
    }

    if(handCol){
      const handHeading=dlFindHeading('Made by hand, one strap at a time');
      set(handCol,'display','flex');
      set(handCol,'flex-direction','column');
      if(handHeading){
        set(handHeading,'margin-top','0');
        set(handHeading,'margin-bottom','8px');
      }
      const image=handCol.querySelector('img');
      if(image){
        set(image,'margin-top','6px');
        set(image,'margin-bottom','8px');
      }
    }

    const run=()=>{
      try{
        const detailsHeading=dlFindHeading('From material to finished piece');
        const handHeading=dlFindHeading('Made by hand, one strap at a time');
        const fitImg=fitCol && fitCol.querySelector('.daluca-ready-lug-watch-image');

        /* Align the middle title with the right-side title. */
        if(handCol && handHeading && detailsHeading){
          set(handCol,'transform','translateY(0px)');
          const dy=detailsHeading.getBoundingClientRect().top-handHeading.getBoundingClientRect().top;
          set(handCol,'transform','translateY('+Math.round(dy)+'px)');
        }

        /* Pull the complete lug-width composition upward. The watch image
           starts just beneath the title line used by the other two columns. */
        if(fitCol && fitImg && detailsHeading){
          set(fitCol,'transform','translateY(0px)');
          const desiredTop=detailsHeading.getBoundingClientRect().top+34;
          const dy=desiredTop-fitImg.getBoundingClientRect().top;
          set(fitCol,'transform','translateY('+Math.round(dy)+'px)');
        }

        /* Make the FULL-WIDTH khaki shell end shortly after whichever column
           actually extends lowest after the transforms above. */
        requestAnimationFrame(()=>{
          const shellRect=shell.getBoundingClientRect();
          let maxBottom=shellRect.top;

          [fitCol,handCol,detailsCol].forEach(col=>{
            if(!col) return;
            const r=col.getBoundingClientRect();
            if(r.height>0) maxBottom=Math.max(maxBottom,r.bottom);
          });

          const target=Math.ceil(maxBottom-shellRect.top+18);
          if(target>0){
            set(shell,'height',target+'px');
            set(shell,'min-height',target+'px');
            set(shell,'max-height',target+'px');
          }
        });
      }catch(e){}
    };

    requestAnimationFrame(run);
    setTimeout(run,80);
    setTimeout(run,260);

    Array.from(detailsCol ? detailsCol.querySelectorAll('img') : []).forEach(img=>{
      if(!img.complete){
        img.addEventListener('load',run,{once:true});
      }
    });
  }


  function dlLeafText(root,re){
    if(!root) return null;
    return Array.from(root.querySelectorAll('*')).find(el=>
      el.children.length===0 && re.test(clean(window.DaLucaI18n.sourceText(el)))
    ) || null;
  }

  function dlCleanClone(node){
    if(!node) return null;
    const c=node.cloneNode(true);
    c.removeAttribute('id');
    c.removeAttribute('style');
    c.querySelectorAll('[id]').forEach(el=>el.removeAttribute('id'));
    c.querySelectorAll('[style]').forEach(el=>el.removeAttribute('style'));
    return c;
  }

  function dlBestParagraph(root){
    if(!root) return null;
    const ps=Array.from(root.querySelectorAll('p')).filter(p=>{
      const t=clean(window.DaLucaI18n.sourceText(p));
      return t.length>35 &&
        !/lug\s*width/i.test(t) &&
        !/inside our san diego workshop/i.test(t) &&
        !/details behind the strap/i.test(t);
    });
    return ps[0] || null;
  }

  function dlFindLink(root,re){
    if(!root) return null;
    return Array.from(root.querySelectorAll('a')).find(a=>re.test(clean(window.DaLucaI18n.sourceText(a)))) || null;
  }

  function dlDetailsCards(detailsCol){
    if(!detailsCol) return [];

    const imgs=Array.from(detailsCol.querySelectorAll('img')).filter(img=>{
      const r=img.getBoundingClientRect();
      return r.width>40 && r.height>40;
    }).slice(0,6);

    return imgs.map((img,index)=>{
      let node=img.parentElement;
      let chosen=node;

      while(node && node!==detailsCol){
        const imageCount=node.querySelectorAll ? node.querySelectorAll('img').length : 0;
        const t=clean(window.DaLucaI18n.sourceText(node));
        if(imageCount===1 && t.length<=90) chosen=node;
        if(imageCount>1) break;
        node=node.parentElement;
      }

      let caption=clean(chosen && window.DaLucaI18n.sourceText(chosen));
      if(!caption || caption.length>90){
        caption=clean(img.alt);
      }
      if(!caption){
        const defaults=[
          '01 SELECTED LEATHER',
          '02 CUT BY HAND',
          '03 HAND STITCHED',
          '04 EDGES REFINED',
          '05 HARDWARE FITTED',
          '06 BUILT TO LAST'
        ];
        caption=defaults[index] || '';
      }

      return {img,caption};
    });
  }

  function rebuildReadyWorkshop(fitHeading,handHeading,detailsHeading,fitCol,handCol,detailsCol,oldSection){
    if(!fitHeading || !handHeading || !detailsHeading || !fitCol || !handCol || !detailsCol || !oldSection) return;
    if(document.querySelector('.daluca-workshop-rebuilt-shell')) return;

    // Locate the widest khaki ancestor so the old block can be replaced,
    // regardless of whether the theme added an extra max-width wrapper.
    let oldShell=oldSection;
    let n=oldSection;
    const viewport=document.documentElement.clientWidth;

    for(let i=0; n && n!==document.body && i<8; i++,n=n.parentElement){
      const r=n.getBoundingClientRect();
      const bg=getComputedStyle(n).backgroundColor;
      if(r.width>=viewport*.92 && bg && bg!=='rgba(0, 0, 0, 0)' && bg!=='transparent'){
        oldShell=n;
      }
    }

    const shell=document.createElement('section');
    shell.className='daluca-workshop-rebuilt-shell';

    const inner=document.createElement('div');
    inner.className='daluca-workshop-rebuilt';

    /* ---------- LEFT / FIT ---------- */
    const fit=document.createElement('div');
    fit.className='daluca-workshop-rebuilt__col daluca-workshop-rebuilt__fit';

    const oldWatch=fitCol.querySelector('.daluca-ready-lug-watch-image');
    if(oldWatch){
      const watch=dlCleanClone(oldWatch);
      watch.className='daluca-workshop-rebuilt__watch';
      watch.alt='';
      watch.setAttribute('aria-hidden','true');
      fit.appendChild(watch);
    }

    const measured=document.createElement('div');
    measured.className='daluca-workshop-rebuilt__eyebrow';
    window.DaLucaI18n.setText(measured, 'MEASURED FOR YOUR WATCH');
    fit.appendChild(measured);

    const lug=document.createElement('div');
    lug.className='daluca-workshop-rebuilt__lug';
    window.DaLucaI18n.setText(lug, 'Lug Width');
    fit.appendChild(lug);

    const fitTitle=dlCleanClone(fitHeading);
    fitTitle.className='daluca-workshop-rebuilt__title';
    fit.appendChild(fitTitle);

    const fitCopy=dlBestParagraph(fitCol);
    if(fitCopy){
      const p=dlCleanClone(fitCopy);
      p.className='daluca-workshop-rebuilt__copy';
      fit.appendChild(p);
    }

    const fitLink=dlFindLink(fitCol,/open the strap finder/i);
    if(fitLink){
      const a=dlCleanClone(fitLink);
      a.className='daluca-workshop-rebuilt__link';
      fit.appendChild(a);
    }

    /* ---------- MIDDLE / HANDMADE ---------- */
    const hand=document.createElement('div');
    hand.className='daluca-workshop-rebuilt__col daluca-workshop-rebuilt__hand';

    const handEyebrow=document.createElement('div');
    handEyebrow.className='daluca-workshop-rebuilt__eyebrow';
    window.DaLucaI18n.setText(handEyebrow, 'INSIDE OUR SAN DIEGO WORKSHOP');
    hand.appendChild(handEyebrow);

    const handTitle=dlCleanClone(handHeading);
    handTitle.className='daluca-workshop-rebuilt__title';
    hand.appendChild(handTitle);

    const handImg=handCol.querySelector('img');
    if(handImg){
      const img=dlCleanClone(handImg);
      img.className='daluca-workshop-rebuilt__hero';
      hand.appendChild(img);
    }

    const handCopy=dlBestParagraph(handCol);
    if(handCopy){
      const p=dlCleanClone(handCopy);
      p.className='daluca-workshop-rebuilt__copy';
      hand.appendChild(p);
    }

    const handLink=dlFindLink(handCol,/visit the daluca workshop story/i);
    if(handLink){
      const a=dlCleanClone(handLink);
      a.className='daluca-workshop-rebuilt__link';
      hand.appendChild(a);
    }

    /* ---------- RIGHT / DETAILS ---------- */
    const details=document.createElement('div');
    details.className='daluca-workshop-rebuilt__col daluca-workshop-rebuilt__details';

    const detailsEyebrow=document.createElement('div');
    detailsEyebrow.className='daluca-workshop-rebuilt__eyebrow';
    window.DaLucaI18n.setText(detailsEyebrow, 'THE DETAILS BEHIND THE STRAP');
    details.appendChild(detailsEyebrow);

    const detailsTitle=dlCleanClone(detailsHeading);
    detailsTitle.className='daluca-workshop-rebuilt__title';
    details.appendChild(detailsTitle);

    const grid=document.createElement('div');
    grid.className='daluca-workshop-rebuilt__grid';

    dlDetailsCards(detailsCol).forEach(({img,caption},index)=>{
      const card=document.createElement('div');
      card.className='daluca-workshop-rebuilt__detail-card';

      const im=dlCleanClone(img);
      im.className='daluca-workshop-rebuilt__detail-img';
      card.appendChild(im);

      const cap=document.createElement('div');
      cap.className='daluca-workshop-rebuilt__caption';
      window.DaLucaI18n.setText(cap, caption);
      card.appendChild(cap);

      grid.appendChild(card);
    });

    details.appendChild(grid);

    inner.append(fit,hand,details);
    shell.appendChild(inner);

    oldShell.insertAdjacentElement('beforebegin',shell);
    oldShell.style.setProperty('display','none','important');
    oldShell.setAttribute('aria-hidden','true');
  }

  function polishLowerWorkshop(){
    if(document.querySelector("[data-daluca-static-fit]")) return;
    const fitHeading=dlFindHeading('Fit, without guessing');
    if(fitHeading){
      let card=fitHeading.closest('.daluca-craft-journey__fit');

      if(!card){
        let probe=fitHeading.parentElement;
        for(let i=0; probe && i<7; i++,probe=probe.parentElement){
          const t=clean(window.DaLucaI18n.sourceText(probe));
          if(/measured for your watch/i.test(t) && /fit,\s*without guessing/i.test(t) && t.length<1400){
            card=probe;
            break;
          }
        }
      }

      if(card){
        card.classList.add('daluca-ready-lug-fit-card');

        // Remove every pre-existing Lug Width label so there is only one.
        Array.from(card.querySelectorAll('p,span,div,small')).forEach(el=>{
          if(/^lug\s*width$/i.test(clean(window.DaLucaI18n.sourceText(el)))) el.remove();
        });

        const measured=Array.from(card.querySelectorAll('p,span,div,small')).find(el=>
          /^measured\s+for\s+your\s+watch$/i.test(clean(window.DaLucaI18n.sourceText(el)))
        );

        const label=document.createElement('div');
        window.DaLucaI18n.setText(label, 'Lug Width');
        label.className='daluca-ready-lug-width-label';
        fitHeading.insertAdjacentElement('beforebegin',label);

        // Place the watch image above the "Measured for your watch" eyebrow
        // instead of above the Lug Width label.
        let img=card.querySelector('.daluca-ready-lug-watch-image');
        if(!img){
          img=ensureDalucaLugWatchImage(card,label,fitHeading);
        }else{
          ensureDalucaLugWatchImage(card,label,fitHeading);
        }

        if(img && measured){
          measured.insertAdjacentElement('beforebegin',img);
        }

        document.querySelectorAll('.daluca-ready-lug-width-label').forEach(el=>{
          if(!card.contains(el)) el.classList.remove('daluca-ready-lug-width-label');
        });

        Array.from(card.querySelectorAll('p,span,div,small')).forEach(el=>{
          if(/^watch lug width reference$/i.test(clean(window.DaLucaI18n.sourceText(el)))){
            el.remove();
          }
        });
      }
    }

    const workshopHeading=dlFindHeading('From material to finished piece');
    if(workshopHeading){
      let section=workshopHeading.parentElement;
      for(let i=0; section && i<7; i++,section=section.parentElement){
        const text=clean(window.DaLucaI18n.sourceText(section));
        if(/made by hand, one strap at a time/i.test(text) && /selected leather/i.test(text)){
          section.classList.add('daluca-ready-workshop-band');

          // Mark the three direct workshop columns so spacing can be tightened
          // without relying on brittle nth-child selectors.
          const directWorkshopChild=(node)=>{
            let el=node;
            while(el && el.parentElement && el.parentElement!==section){
              el=el.parentElement;
            }
            return el && el.parentElement===section ? el : null;
          };

          const fitCol=directWorkshopChild(card);
          if(fitCol) fitCol.classList.add('daluca-ready-workshop-fit-col');

          const handHeading=dlFindHeading('Made by hand, one strap at a time');
          const handCol=directWorkshopChild(handHeading);
          if(handCol) handCol.classList.add('daluca-ready-workshop-hand-col');

          const detailsCol=directWorkshopChild(workshopHeading);
          if(detailsCol) detailsCol.classList.add('daluca-ready-workshop-details-col');

          rebuildReadyWorkshop(
            fitHeading,
            handHeading,
            workshopHeading,
            fitCol,
            handCol,
            detailsCol,
            section
          );

          hardBalanceReadyWorkshop(section,fitCol,handCol,detailsCol);

          let outer=section;
          for(let j=0; outer && j<6; j++,outer=outer.parentElement){
            if(
              outer.tagName==='SECTION' ||
              outer.classList.contains('shopify-section') ||
              /section/i.test(outer.className || '')
            ){
              outer.classList.add('daluca-ready-workshop-section');
              setTimeout(()=>hardBalanceReadyWorkshop(section,fitCol,handCol,detailsCol),30);
              setTimeout(()=>finalWorkshopGeometry(section,fitCol,handCol,detailsCol),50);
              break;
            }
          }
          break;
        }
      }
    }
  }


  function rebalanceReadyWorkshopOnResize(){
    const section=document.querySelector('.daluca-ready-workshop-band');
    if(!section) return;
    const fitCol=section.querySelector('.daluca-ready-workshop-fit-col');
    const handCol=section.querySelector('.daluca-ready-workshop-hand-col');
    const detailsCol=section.querySelector('.daluca-ready-workshop-details-col');

    hardBalanceReadyWorkshop(section,fitCol,handCol,detailsCol);
    setTimeout(()=>finalWorkshopGeometry(section,fitCol,handCol,detailsCol),40);
  }

  if(!window.__dalucaReadyWorkshopResizeBound){
    window.__dalucaReadyWorkshopResizeBound=true;
    window.addEventListener('resize',()=>{
      clearTimeout(window.__dalucaReadyWorkshopResizeTimer);
      window.__dalucaReadyWorkshopResizeTimer=setTimeout(rebalanceReadyWorkshopOnResize,120);
    },{passive:true});
  }


  function dlLowestCommonAncestor(nodes){
    const valid=nodes.filter(Boolean);
    if(!valid.length) return null;

    let a=valid[0];
    while(a && a!==document.body){
      if(valid.every(n=>a.contains(n))) return a;
      a=a.parentElement;
    }
    return null;
  }

  function dlDirectChildOf(ancestor,node){
    if(!ancestor || !node) return null;
    let el=node;
    while(el && el.parentElement && el.parentElement!==ancestor){
      el=el.parentElement;
    }
    return el && el.parentElement===ancestor ? el : null;
  }

  function dlLeafExact(root,re){
    if(!root) return [];
    return Array.from(root.querySelectorAll('*')).filter(el=>
      el.children.length===0 && re.test(clean(window.DaLucaI18n.sourceText(el)))
    );
  }

  function dlLiveWorkshopFix(){
    if(document.querySelector("[data-daluca-static-fit]")) return;
    if(window.innerWidth<768) return;

    const fitHeading=dlFindHeading('Fit, without guessing');
    const handHeading=dlFindHeading('Made by hand, one strap at a time');
    const detailsHeading=dlFindHeading('From material to finished piece');

    if(!fitHeading || !handHeading || !detailsHeading) return;

    // Use the REAL lowest common ancestor of the three visible columns.
    // This avoids guessing which theme wrapper is the workshop row.
    const row=dlLowestCommonAncestor([fitHeading,handHeading,detailsHeading]);
    if(!row) return;

    const fitCol=dlDirectChildOf(row,fitHeading);
    const handCol=dlDirectChildOf(row,handHeading);
    const detailsCol=dlDirectChildOf(row,detailsHeading);
    if(!fitCol || !handCol || !detailsCol) return;

    row.classList.add('daluca-live-workshop-row');
    fitCol.classList.add('daluca-live-workshop-fit');
    handCol.classList.add('daluca-live-workshop-hand');
    detailsCol.classList.add('daluca-live-workshop-details');

    // Find the actual full-width khaki background ancestor.
    const rowBg=getComputedStyle(row).backgroundColor;
    let shell=row;
    let n=row.parentElement;
    const vw=document.documentElement.clientWidth;

    for(let i=0; n && n!==document.body && i<8; i++,n=n.parentElement){
      const r=n.getBoundingClientRect();
      const bg=getComputedStyle(n).backgroundColor;
      if(r.width>=vw*.92 && bg && bg!=='transparent' && bg!=='rgba(0, 0, 0, 0)'){
        shell=n;
        break;
      }
      if(bg===rowBg && r.width>shell.getBoundingClientRect().width){
        shell=n;
      }
    }
    shell.classList.add('daluca-live-workshop-shell');

    const set=(el,p,v)=>{
      if(el) el.style.setProperty(p,v,'important');
    };

    // Cancel old pseudo lug visual and normalize left content.
    fitCol.classList.add('daluca-ready-lug-fit-card');
    dlLeafExact(fitCol,/^lug\s*width$/i).forEach(el=>el.remove());

    let label=document.createElement('div');
    label.className='daluca-live-lug-label';
    window.DaLucaI18n.setText(label, 'Lug Width');
    fitHeading.insertAdjacentElement('beforebegin',label);

    const measured=dlLeafExact(fitCol,/^measured\s+for\s+your\s+watch$/i)[0] || null;
    let watch=fitCol.querySelector('.daluca-ready-lug-watch-image');

    if(!watch){
      watch=ensureDalucaLugWatchImage(fitCol,label,fitHeading);
    }

    if(measured){
      if(watch){
        measured.insertAdjacentElement('afterend',watch);
        watch.insertAdjacentElement('afterend',label);
      }else{
        measured.insertAdjacentElement('afterend',label);
      }
    }else if(watch){
      watch.insertAdjacentElement('afterend',label);
    }

    // Hard-reset the actual row and columns with inline !important geometry.
    set(row,'display','grid');
    set(row,'grid-template-columns','1.3fr 1fr 1.7fr');
    set(row,'align-items','start');
    set(row,'height','auto');
    set(row,'min-height','0');
    set(row,'max-height','none');
    set(row,'padding-top','16px');
    set(row,'padding-bottom','14px');
    set(row,'margin-top','0');
    set(row,'margin-bottom','0');
    set(row,'overflow','visible');

    [fitCol,handCol,detailsCol].forEach(col=>{
      set(col,'align-self','start');
      set(col,'justify-content','flex-start');
      set(col,'height','auto');
      set(col,'min-height','0');
      set(col,'max-height','none');
      set(col,'margin-top','0');
      set(col,'margin-bottom','0');
      set(col,'padding-top','0');
      set(col,'padding-bottom','0');
      set(col,'transform','none');
      set(col,'overflow','visible');
    });

    // Left column: center the complete composition vertically.
    // IMPORTANT: this is inside dlLiveWorkshopFix itself, because that
    // function is the last workshop pass to run and previously kept
    // resetting the column back to top-aligned.
    set(fitCol,'display','flex');
    set(fitCol,'flex-direction','column');
    set(fitCol,'align-items','center');
    set(fitCol,'justify-content','center');
    set(fitCol,'text-align','center');
    set(fitCol,'padding-top','0');
    set(fitCol,'padding-bottom','0');

    if(watch){
      set(watch,'width','300px');
      set(watch,'height','auto');
      set(watch,'margin','0 auto 4px');
      set(watch,'object-fit','contain');
    }
    if(measured){
      set(measured,'margin','0 0 5px');
    }
    set(label,'margin','0 0 6px');
    if(watch){const offset=watch.getBoundingClientRect().height*.163+11;set(label,'transform','translateY(-'+offset+'px)');set(label,'margin-bottom',(6-offset)+'px');}
    set(fitHeading,'margin','0 0 8px');
    set(fitHeading,'text-align','center');
    Array.from(fitCol.querySelectorAll('p')).forEach(p=>{set(p,'text-align','center');set(p,'max-width','330px');set(p,'margin-left','auto');set(p,'margin-right','auto');});

    const fitPs=Array.from(fitCol.querySelectorAll('p'));
    fitPs.forEach(p=>{
      set(p,'margin-top','0');
      set(p,'margin-bottom','8px');
    });

    // Middle column: title/image/copy all start higher.
    set(handCol,'display','flex');
    set(handCol,'flex-direction','column');
    set(handHeading,'margin-top','0');
    set(handHeading,'margin-bottom','8px');

    const handImg=handCol.querySelector('img');
    if(handImg){
      set(handImg,'display','block');
      set(handImg,'margin-top','6px');
      set(handImg,'margin-bottom','8px');
      set(handImg,'padding','0');
      set(handImg,'border','0');
      set(handImg,'outline','0');
      set(handImg,'box-shadow','none');
      set(handImg,'background','transparent');
      set(handImg,'vertical-align','top');

      // Strip the thin white/border lines from simple image wrappers too.
      let mediaWrap=handImg.parentElement;
      for(let i=0; mediaWrap && mediaWrap!==handCol && i<4; i++,mediaWrap=mediaWrap.parentElement){
        set(mediaWrap,'border-top','0');
        set(mediaWrap,'border-bottom','0');
        set(mediaWrap,'outline','0');
        set(mediaWrap,'box-shadow','none');
        set(mediaWrap,'background','transparent');
        set(mediaWrap,'padding-top','0');
        set(mediaWrap,'padding-bottom','0');
        set(mediaWrap,'line-height','0');
      }
    }
    Array.from(handCol.querySelectorAll('p')).forEach(p=>{
      set(p,'margin-top','0');
      set(p,'margin-bottom','8px');
    });

    // Right column defines natural height and remains top aligned.
    set(detailsHeading,'margin-top','0');

    // Put borders on the true full-width khaki shell.
    set(shell,'border-top','1px solid rgba(30,47,63,.15)');
    set(shell,'border-bottom','1px solid rgba(30,47,63,.15)');
    set(shell,'box-shadow','none');
    set(shell,'height','auto');
    set(shell,'min-height','0');
    set(shell,'max-height','none');

    // Remove inherited border from inner row so there is only one continuous line.
    set(row,'border-top','0');
    set(row,'border-bottom','0');
    set(row,'box-shadow','none');

    // First match the left column to the rendered height of the right
    // workshop grid, then center the complete lug-width block inside it.
    requestAnimationFrame(()=>{
      try{
        const detailsHeight=Math.round(detailsCol.getBoundingClientRect().height);

        if(detailsHeight>0){
          set(fitCol,'height','auto');
          set(fitCol,'min-height',detailsHeight+'px');
          set(fitCol,'max-height','none');
          set(fitCol,'justify-content','center');
          set(fitCol,'padding-top','0');
          set(fitCol,'padding-bottom','0');
        }

        // Wait one paint so the centered position is included in shell height.
        requestAnimationFrame(()=>{
          try{
            const shellRect=shell.getBoundingClientRect();
            let bottom=shellRect.top;

            [fitCol,handCol,detailsCol].forEach(col=>{
              const r=col.getBoundingClientRect();
              if(r.height>0) bottom=Math.max(bottom,r.bottom);
            });

            const target=Math.ceil(bottom-shellRect.top+14);
            if(target>0){
              set(shell,'height',target+'px');
              set(shell,'min-height',target+'px');
              set(shell,'max-height',target+'px');
              set(shell,'overflow','hidden');
            }
          }catch(e){}
        });
      }catch(e){}
    });
  }

  function dlScheduleLiveWorkshopFix(){
    [0,80,250,700].forEach(ms=>setTimeout(()=>{
      try{ dlLiveWorkshopFix(); }catch(e){}
    },ms));
  }

  if(document.readyState==='loading'){
    document.addEventListener('DOMContentLoaded',dlScheduleLiveWorkshopFix,{once:true});
  }else{
    dlScheduleLiveWorkshopFix();
  }

  if(!window.__dalucaLiveWorkshopResize){
    window.__dalucaLiveWorkshopResize=true;
    window.addEventListener('resize',()=>{
      clearTimeout(window.__dalucaLiveWorkshopResizeTimer);
      window.__dalucaLiveWorkshopResizeTimer=setTimeout(dlLiveWorkshopFix,120);
    },{passive:true});
  }


  function dlFinalCenterKhakiLeft(){
    if(document.querySelector("[data-daluca-static-fit]")) return;
    if(window.innerWidth < 768) return;

    const fitHeading=dlFindHeading('Fit, without guessing');
    const detailsHeading=dlFindHeading('From material to finished piece');
    if(!fitHeading || !detailsHeading) return;

    const row=dlLowestCommonAncestor([fitHeading,detailsHeading]);
    if(!row) return;

    const fitCol=dlDirectChildOf(row,fitHeading);
    const detailsCol=dlDirectChildOf(row,detailsHeading);
    if(!fitCol || !detailsCol) return;

    const set=(el,p,v)=>{
      if(el) el.style.setProperty(p,v,'important');
    };

    set(fitCol,'transform','none');
    set(fitCol,'position','relative');
    set(fitCol,'display','flex');
    set(fitCol,'flex-direction','column');
    set(fitCol,'align-items','center');
    set(fitCol,'justify-content','center');
    set(fitCol,'text-align','center');
    set(fitCol,'padding-top','0');
    set(fitCol,'padding-bottom','0');
    set(fitCol,'margin-top','0');
    set(fitCol,'margin-bottom','0');

    const detailsHeight=Math.round(detailsCol.getBoundingClientRect().height);
    if(detailsHeight > 0){
      set(fitCol,'height','auto');
      set(fitCol,'min-height',detailsHeight+'px');
      set(fitCol,'max-height','none');
    }

    const watch=fitCol.querySelector('.daluca-ready-lug-watch-image');
    const measured=dlLeafExact(fitCol,/^measured\\s+for\\s+your\\s+watch$/i)[0] || null;
    const lug=fitCol.querySelector('.daluca-live-lug-label') || fitCol.querySelector('.daluca-ready-lug-width-label');

    if(measured) set(measured,'margin','0 0 5px');
    if(watch) set(watch,'margin','3px auto 5px');
    if(lug){
      set(lug,'margin','0 0 7px');
      if(watch){const offset=watch.getBoundingClientRect().height*.163+12;set(lug,'transform','translateY(-'+offset+'px)');set(lug,'margin-bottom',(7-offset)+'px');}
    }
    set(fitHeading,'margin','0 0 8px');
    set(fitHeading,'text-align','center');
    Array.from(fitCol.querySelectorAll('p')).forEach(p=>{set(p,'text-align','center');set(p,'max-width','330px');set(p,'margin-left','auto');set(p,'margin-right','auto');});

    const cta=Array.from(fitCol.querySelectorAll('a')).find(a=>/open the strap finder/i.test(clean(window.DaLucaI18n.sourceText(a))));
    if(cta){
      set(cta,'margin-top','6px');
      set(cta,'margin-bottom','0');
    }
  }

  function dlScheduleFinalKhakiCenter(){
    [0,80,220,600].forEach(ms=>setTimeout(()=>{
      try{ dlFinalCenterKhakiLeft(); }catch(e){}
    },ms));
  }

  if(document.readyState==='loading'){
    document.addEventListener('DOMContentLoaded',dlScheduleFinalKhakiCenter,{once:true});
  }else{
    dlScheduleFinalKhakiCenter();
  }

  if(!window.__dalucaFinalKhakiCenterResize){
    window.__dalucaFinalKhakiCenterResize=true;
    window.addEventListener('resize',()=>{
      clearTimeout(window.__dalucaFinalKhakiCenterTimer);
      window.__dalucaFinalKhakiCenterTimer=setTimeout(dlFinalCenterKhakiLeft,120);
    },{passive:true});
  }


  function dlSmartGalleryBackground(data,w,h){
    const pts=[];
    const patch=Math.max(4,Math.round(Math.min(w,h)*0.025));
    const corners=[
      [0,0],
      [Math.max(0,w-patch),0],
      [0,Math.max(0,h-patch)],
      [Math.max(0,w-patch),Math.max(0,h-patch)]
    ];

    corners.forEach(([sx,sy])=>{
      for(let y=sy;y<Math.min(h,sy+patch);y+=2){
        for(let x=sx;x<Math.min(w,sx+patch);x+=2){
          const i=(y*w+x)*4;
          if(data[i+3] > 200) pts.push([data[i],data[i+1],data[i+2]]);
        }
      }
    });

    if(!pts.length) return [250,250,250];

    const avg=[0,0,0];
    pts.forEach(p=>{
      avg[0]+=p[0];
      avg[1]+=p[1];
      avg[2]+=p[2];
    });
    return avg.map(v=>v/pts.length);
  }

  function dlDetectProductVerticalBounds(img){
    try{
      if(!img || !img.naturalWidth || !img.naturalHeight) return null;

      const maxSide=360;
      const s=Math.min(1,maxSide/Math.max(img.naturalWidth,img.naturalHeight));
      const w=Math.max(1,Math.round(img.naturalWidth*s));
      const h=Math.max(1,Math.round(img.naturalHeight*s));

      const canvas=document.createElement('canvas');
      canvas.width=w;
      canvas.height=h;
      const ctx=canvas.getContext('2d',{willReadFrequently:true});
      if(!ctx) return null;

      ctx.drawImage(img,0,0,w,h);
      const pixels=ctx.getImageData(0,0,w,h).data;
      const bg=dlSmartGalleryBackground(pixels,w,h);

      const activeRows=[];
      const threshold=24;
      const minPixels=Math.max(5,Math.round(w*0.012));

      for(let y=0;y<h;y++){
        let count=0;

        for(let x=0;x<w;x++){
          const i=(y*w+x)*4;
          if(pixels[i+3] < 80) continue;

          const dr=pixels[i]-bg[0];
          const dg=pixels[i+1]-bg[1];
          const db=pixels[i+2]-bg[2];
          const distance=Math.sqrt(dr*dr+dg*dg+db*db);

          if(distance>threshold){
            count++;
            if(count>=minPixels) break;
          }
        }

        if(count>=minPixels) activeRows.push(y);
      }

      if(!activeRows.length) return null;

      let top=activeRows[0];
      let bottom=activeRows[activeRows.length-1];

      // Ignore pathological detections. A watch strap should occupy a useful
      // portion of these white-background product images.
      const objectHeight=bottom-top+1;
      if(objectHeight < h*0.12) return null;

      // About 5.5% of the full source height on BOTH sides.
      // On a 1001px source this is roughly 55px of safety above and below,
      // enough for normal photo-to-photo placement variance and soft shadows.
      const safety=Math.max(14,Math.round(h*0.055));
      top=Math.max(0,top-safety);
      bottom=Math.min(h-1,bottom+safety);

      return {
        topFraction: top/h,
        heightFraction: (bottom-top+1)/h
      };
    }catch(e){
      // Canvas pixel access can fail if an image host ever changes CORS policy.
      // The caller uses a conservative centered fallback in that case.
      return null;
    }
  }

  function dlSmartGalleryMainImages(gallery){
    if(!gallery) return [];

    const slider=gallery.querySelector('product-slider.product-images') ||
                 gallery.querySelector('.product-images');

    if(!slider) return [];

    return Array.from(slider.querySelectorAll('img')).filter(img=>{
      if(img.closest('scroll-shadow')) return false;
      if(img.closest('[class*="thumbnail"],[class*="thumb"]')) return false;
      return true;
    });
  }

  function dlApplySmartGallery(){
    const r=root();
    if(!r || !isVintageReady(r) || window.innerWidth<768) return;

    const gallery=r.querySelector(':scope > .product-gallery') || r.querySelector('.product-gallery');
    if(!gallery) return;

    const images=dlSmartGalleryMainImages(gallery);
    if(!images.length) return;

    const desktop=window.innerWidth>=1181;
    const targetWidth=desktop ? 900 : 760;
    const viewportAllowance=desktop ? 150 : 90;
    const displayWidth=Math.max(
      520,
      Math.min(targetWidth, window.innerWidth-viewportAllowance)
    );

    let maxCropHeight=0;
    let processed=0;

    images.forEach(img=>{
      const process=()=>{
        if(!img.naturalWidth || !img.naturalHeight) return;

        const detected=dlDetectProductVerticalBounds(img);

        // Fallback: a very conservative centered 72% vertical window.
        // This trims only ~14% from top and bottom, so even a substantially
        // shifted strap remains safe if pixel detection is unavailable.
        const topFraction=detected ? detected.topFraction : 0.14;
        const heightFraction=detected ? detected.heightFraction : 0.72;

        const actualWidth=Math.min(displayWidth,img.naturalWidth);
        const fullRenderedHeight=actualWidth*(img.naturalHeight/img.naturalWidth);
        const cropTop=Math.max(0,fullRenderedHeight*topFraction);
        const cropHeight=Math.min(
          fullRenderedHeight,
          Math.max(260,fullRenderedHeight*heightFraction)
        );

        img.classList.add('daluca-smart-gallery-image');
        img.style.setProperty('--dl-smart-image-width',actualWidth+'px');
        img.style.setProperty('--dl-smart-crop-top',cropTop+'px');

        const media=img.closest(
          '.product-images__slide, .product-single__media, .product-single__media-image, [data-media-id]'
        ) || img.parentElement;

        const picture=img.closest('picture');

        if(media){
          media.classList.add('daluca-smart-gallery-media');
          media.style.setProperty('--dl-smart-media-height',Math.ceil(cropHeight)+'px');
        }

        if(picture){
          picture.classList.add('daluca-smart-gallery-picture');
          picture.style.setProperty('--dl-smart-media-height',Math.ceil(cropHeight)+'px');
        }

        maxCropHeight=Math.max(maxCropHeight,Math.ceil(cropHeight));
        processed++;
      };

      if(img.complete && img.naturalWidth){
        process();
      }else{
        img.addEventListener('load',()=>{
          process();
          setTimeout(dlFinalizeSmartGallery,0);
        },{once:true});
      }
    });

    function dlFinalizeSmartGallery(){
      if(!maxCropHeight) return;

      const safeOuter=maxCropHeight+2;
      r.classList.add('daluca-smart-gallery-ready');
      r.style.setProperty('--dl-smart-gallery-height',safeOuter+'px');
      r.style.setProperty('--dl-smart-gallery-width',displayWidth+'px');

      const slider=gallery.querySelector('product-slider.product-images') ||
                   gallery.querySelector('.product-images');
      const imageContainer=gallery.querySelector('.product-image-container');

      [slider,imageContainer].forEach(el=>{
        if(!el) return;
        el.classList.add('daluca-smart-gallery-viewport');
        el.style.setProperty('--dl-smart-gallery-height',safeOuter+'px');
      });
    }

    if(processed) dlFinalizeSmartGallery();
  }

  function dlScheduleSmartGallery(){
    [0,80,250,650].forEach(ms=>setTimeout(()=>{
      try{ dlApplySmartGallery(); }catch(e){}
    },ms));
  }

  if(document.readyState==='loading'){
    document.addEventListener('DOMContentLoaded',dlScheduleSmartGallery,{once:true});
  }else{
    dlScheduleSmartGallery();
  }

  if(!window.__dalucaSmartGalleryResizeBound){
    window.__dalucaSmartGalleryResizeBound=true;
    window.addEventListener('resize',()=>{
      clearTimeout(window.__dalucaSmartGalleryResizeTimer);
      window.__dalucaSmartGalleryResizeTimer=setTimeout(dlApplySmartGallery,140);
    },{passive:true});
  }

  document.addEventListener('click',e=>{
    if(e.target && e.target.closest('.product-gallery')){
      setTimeout(dlApplySmartGallery,90);
    }
  });


  /* =========================================================
     DIRECT HABITAT GALLERY FIX
     Based on the live DOM from the store:
       .product-gallery
       .product-gallery-container
       .product-image-container
       product-slider.product-images
       .flickity-viewport
       .product-images__slide
       .product-single__media-image
       img.lazyautosizes

     The live main image was only rendering ~349x349 inside a 1460px
     gallery. This targets that actual image and its Flickity viewport
     directly rather than relying on the earlier inferred selectors.
     ========================================================= */

  function dlDirectGalleryBg(data,w,h){
    const sample=[];
    const patch=Math.max(5,Math.round(Math.min(w,h)*0.025));
    const starts=[
      [0,0],
      [Math.max(0,w-patch),0],
      [0,Math.max(0,h-patch)],
      [Math.max(0,w-patch),Math.max(0,h-patch)]
    ];

    starts.forEach(([sx,sy])=>{
      for(let y=sy;y<Math.min(h,sy+patch);y+=2){
        for(let x=sx;x<Math.min(w,sx+patch);x+=2){
          const i=(y*w+x)*4;
          if(data[i+3]>200) sample.push([data[i],data[i+1],data[i+2]]);
        }
      }
    });

    if(!sample.length) return [250,250,250];

    const sum=[0,0,0];
    sample.forEach(p=>{
      sum[0]+=p[0]; sum[1]+=p[1]; sum[2]+=p[2];
    });
    return sum.map(v=>v/sample.length);
  }

  function dlDirectGalleryBounds(img){
    try{
      if(!img || !img.naturalWidth || !img.naturalHeight) return null;

      const maxSide=420;
      const scale=Math.min(1,maxSide/Math.max(img.naturalWidth,img.naturalHeight));
      const w=Math.max(1,Math.round(img.naturalWidth*scale));
      const h=Math.max(1,Math.round(img.naturalHeight*scale));

      const canvas=document.createElement('canvas');
      canvas.width=w;
      canvas.height=h;

      const ctx=canvas.getContext('2d',{willReadFrequently:true});
      if(!ctx) return null;

      ctx.drawImage(img,0,0,w,h);
      const px=ctx.getImageData(0,0,w,h).data;
      const bg=dlDirectGalleryBg(px,w,h);

      const threshold=22;
      const rowNeed=Math.max(5,Math.round(w*.01));
      const colNeed=Math.max(5,Math.round(h*.01));
      const rows=[];
      const cols=[];

      for(let y=0;y<h;y++){
        let count=0;
        for(let x=0;x<w;x++){
          const i=(y*w+x)*4;
          if(px[i+3]<80) continue;
          const dr=px[i]-bg[0], dg=px[i+1]-bg[1], db=px[i+2]-bg[2];
          if(Math.sqrt(dr*dr+dg*dg+db*db)>threshold){
            count++;
            if(count>=rowNeed) break;
          }
        }
        if(count>=rowNeed) rows.push(y);
      }

      for(let x=0;x<w;x++){
        let count=0;
        for(let y=0;y<h;y++){
          const i=(y*w+x)*4;
          if(px[i+3]<80) continue;
          const dr=px[i]-bg[0], dg=px[i+1]-bg[1], db=px[i+2]-bg[2];
          if(Math.sqrt(dr*dr+dg*dg+db*db)>threshold){
            count++;
            if(count>=colNeed) break;
          }
        }
        if(count>=colNeed) cols.push(x);
      }

      if(!rows.length || !cols.length) return null;

      let top=rows[0], bottom=rows[rows.length-1];
      let left=cols[0], right=cols[cols.length-1];

      if((right-left)<w*.18 || (bottom-top)<h*.025) return null;

      // Conservative safety zone for normal photography-session movement,
      // soft shadows and bright buckle edges.
      const padX=Math.max(16,Math.round(w*.05));
      const padY=Math.max(16,Math.round(h*.06));

      left=Math.max(0,left-padX);
      right=Math.min(w-1,right+padX);
      top=Math.max(0,top-padY);
      bottom=Math.min(h-1,bottom+padY);

      return {
        left:left/w,
        top:top/h,
        width:(right-left+1)/w,
        height:(bottom-top+1)/h
      };
    }catch(e){
      return null;
    }
  }

  function dlPromoteShopifyImage(img,targetWidth){
    return new Promise(resolve=>{
      try{
        const raw=img.currentSrc || img.src || '';
        if(!raw){
          resolve(false);
          return;
        }

        let promoted=raw;
        if(/([?&])width=\d+/i.test(promoted)){
          promoted=promoted.replace(/([?&])width=\d+/i,'$1width='+targetWidth);
        }else{
          promoted += (promoted.includes('?')?'&':'?')+'width='+targetWidth;
        }

        // Preload first so we never visibly stretch the old smaller source.
        const pre=new Image();
        pre.onload=()=>{
          try{
            img.setAttribute('sizes',targetWidth+'px');
            img.setAttribute('data-sizes',targetWidth+'px');
            img.removeAttribute('data-srcset');
            img.srcset=promoted+' '+targetWidth+'w';
            img.src=promoted;
          }catch(e){}
          resolve(true);
        };
        pre.onerror=()=>resolve(false);
        pre.src=promoted;
      }catch(e){
        resolve(false);
      }
    });
  }

  function dlRestoreDirectVideoGallery(gallery){
    const state=gallery.__dlDirectVideoSize;
    if(!state) return false;
    state.elements.forEach(entry=>{
      entry.properties.forEach(property=>{
        if(entry.element.style.getPropertyValue(property.name)!==state.height || entry.element.style.getPropertyPriority(property.name)!=='important') return;
        if(property.value){
          entry.element.style.setProperty(property.name,property.value,property.priority);
        }else{
          entry.element.style.removeProperty(property.name);
        }
      });
    });
    gallery.__dlDirectVideoSize=null;
    return true;
  }

  function dlResizeDirectGallery(slider){
    const flkty=slider.flkty;
    if(!flkty || typeof flkty.resize!=='function') return;
    flkty.resize();
    if((flkty.isAnimating || flkty.isDragging) && typeof flkty.once==='function'){
      flkty.once('settle',()=>{
        if(slider.isConnected && slider.flkty===flkty) flkty.resize();
      });
    }
  }

  function dlApplyDirectGallery(){
    const r=root();
    if(!r) return;

    const gallery=r.querySelector('.product-gallery');
    if(!gallery) return;

    const imageContainer=gallery.querySelector('.product-image-container');
    const slider=gallery.querySelector('product-slider.product-images');
    const flickViewport=gallery.querySelector('.flickity-viewport');
    const slides=Array.from(gallery.querySelectorAll('.product-images__slide'));

    if(!imageContainer || !slider || !flickViewport || !slides.length) return;

    // Preserve the outgoing media's height before restoring video-owned styles.
    const previousViewportHeight=Math.ceil(flickViewport.getBoundingClientRect().height);
    const galleryPass=(gallery.__dlDirectGalleryPass || 0)+1;
    gallery.__dlDirectGalleryPass=galleryPass;
    if(window.innerWidth<768){
      const restoredVideo=dlRestoreDirectVideoGallery(gallery);
      const previousHeight=r.style.getPropertyValue('--dl-direct-gallery-h');
      if(previousHeight){
        [imageContainer,slider,flickViewport,...slides].forEach(el=>{
          ['height','min-height','max-height'].forEach(property=>{
            if(el.style.getPropertyValue(property)===previousHeight && el.style.getPropertyPriority(property)==='important'){
              el.style.removeProperty(property);
            }
          });
        });
        r.style.removeProperty('--dl-direct-gallery-h');
      }
      if(restoredVideo || previousHeight) dlResizeDirectGallery(slider);
      return;
    }

    if(!gallery.__dlSelectionObserver){
      gallery.__dlSelectionObserver=new MutationObserver(changes=>{
        if(changes.some(change=>change.target.matches('.product-images__slide') &&
          /is-selected/.test(change.oldValue||'')!==change.target.classList.contains('is-selected'))){
          dlApplyDirectGallery();
        }
      });
      gallery.__dlSelectionObserver.observe(slider,{subtree:true,attributes:true,attributeFilter:['class'],attributeOldValue:true});
    }
    if(!gallery.__dlVideoMetadataBound){
      gallery.__dlVideoMetadataBound=true;
      gallery.addEventListener('loadedmetadata',event=>{
        if(event.target.matches('.product-single__media-native-video > video')){
          dlApplyDirectGallery();
        }
      },true);
    }

    const selectedSlide=gallery.querySelector('.product-images__slide.is-selected');
    const selectedVideo=selectedSlide && selectedSlide.querySelector('.product-single__media-native-video > video');
    if(selectedVideo){
      const videoRatio=selectedVideo.videoWidth>0 && selectedVideo.videoHeight>0
        ? selectedVideo.videoWidth/selectedVideo.videoHeight
        : Number(selectedVideo.parentElement.style.getPropertyValue('--aspect-ratio'));
      const availableWidth=slider.clientWidth;
      if(Number.isFinite(videoRatio) && videoRatio>0 && availableWidth>0){
        const videoWidth=Math.min(1000,availableWidth,selectedVideo.videoWidth || Infinity);
        const videoHeight=Math.min(760,Math.max(440,Math.round(window.innerHeight*.72)),selectedVideo.videoHeight || Infinity);
        const height=Math.max(1,Math.floor(Math.min(videoHeight,videoWidth/videoRatio)))+'px';
        dlRestoreDirectVideoGallery(gallery);
        const elements=[imageContainer,slider,flickViewport,selectedSlide].map(element=>({
          element,
          properties:['height','min-height','max-height'].map(name=>({
            name,value:element.style.getPropertyValue(name),priority:element.style.getPropertyPriority(name)
          }))
        }));
        gallery.__dlDirectVideoSize={height,elements};
        elements.forEach(entry=>{
          entry.properties.forEach(property=>entry.element.style.setProperty(property.name,height,'important'));
        });
        return;
      }
    }
    if(dlRestoreDirectVideoGallery(gallery)) dlResizeDirectGallery(slider);

    const desktop=window.innerWidth>=1181;

    // Request close to native source quality, but DISPLAY below native size.
    const sourceRequest=desktop ? 1000 : 860;
    const fullCanvasDisplayCap=desktop ? 900 : 780;

    // Grand, but not oversized. At ~1700px desktop this is ~700–720px.
    const visibleWidthCap=desktop
      ? Math.min(720,Math.max(660,Math.round(window.innerWidth*.42)))
      : Math.min(650,Math.max(560,Math.round(window.innerWidth*.72)));

    const currentImageSlide=selectedSlide || slides.find(slide=>
      slide.classList.contains('is-active') && slide.querySelector('.product-single__media-image img')
    ) || slides.find(slide=>slide.querySelector('.product-single__media-image img'));
    const frame=currentImageSlide && currentImageSlide.querySelector('.product-single__media-image');
    const img=frame && frame.querySelector('img');
    if(!frame || !img) return;

    const fullBounds={left:0,top:0,width:1,height:1};
    let photoLayoutRevision=0;
    const isCurrentPhoto=()=>{
      const activeSlide=gallery.querySelector('.product-images__slide.is-selected');
      return gallery.__dlDirectGalleryPass===galleryPass && window.innerWidth>=768 &&
        gallery.isConnected && (!activeSlide || activeSlide===currentImageSlide);
    };

    const applyPhotoLayout=b=>{
      if(!isCurrentPhoto()) return;
      const naturalW=img.naturalWidth || Number(img.getAttribute('width'));
      const naturalH=img.naturalHeight || Number(img.getAttribute('height'));
      if(!Number.isFinite(naturalW) || !Number.isFinite(naturalH) || naturalW<=0 || naturalH<=0) return;

      // Set the complete frame before fetching a sharper source. On first load,
      // the image attributes reserve its aspect ratio without waiting for decode.
      const qualityCap=Math.min(fullCanvasDisplayCap,naturalW);
      const neededForVisible=visibleWidthCap/Math.max(.35,b.width);
      const renderW=Math.round(Math.min(qualityCap,Math.max(620,neededForVisible)));
      const renderH=renderW*(naturalH/naturalW);
      const cropLeft=Math.round(renderW*b.left);
      const cropTop=Math.round(renderH*b.top);
      const frameW=Math.round(renderW*b.width);
      const frameH=Math.round(renderH*b.height);
      const heroH=frameH+4;
      const layoutRevision=++photoLayoutRevision;

      currentImageSlide.classList.add('daluca-direct-gallery-slide');
      frame.classList.add('daluca-direct-gallery-frame');
      img.classList.add('daluca-direct-gallery-image');
      frame.style.setProperty('--dl-frame-w',frameW+'px');
      frame.style.setProperty('--dl-frame-h',frameH+'px');
      img.style.setProperty('--dl-render-w',renderW+'px');
      img.style.setProperty('--dl-crop-x',cropLeft+'px');
      img.style.setProperty('--dl-crop-y',cropTop+'px');
      currentImageSlide.dataset.dalucaDirectFrameH=String(frameH);

      // Commit the selected photo and outer viewport together, before paint.
      const sticky=gallery.querySelector('.product-gallery-sticky') ||
        r.querySelector('.product-gallery-sticky');
      const container=gallery.querySelector('.product-gallery-container') ||
        r.querySelector('.product-gallery-container');
      [gallery,sticky,container].forEach(el=>{
        if(!el) return;
        el.style.setProperty('height','auto','important');
        el.style.setProperty('min-height','0','important');
        el.style.setProperty('max-height','none','important');
      });

      r.classList.add('daluca-direct-gallery-non-vintage-tight');
      r.classList.add('daluca-direct-gallery-ready');
      r.classList.add('daluca-direct-gallery-balanced');

      const writePhotoHeight=height=>{
        r.style.setProperty('--dl-direct-gallery-h',height+'px');
        [imageContainer,slider,flickViewport,...slides].forEach(el=>{
          el.style.setProperty('height',height+'px','important');
          el.style.setProperty('min-height',height+'px','important');
          el.style.setProperty('max-height',height+'px','important');
        });
      };

      // A shorter incoming photo must not clip taller media still moving out.
      // Keep this hold on the gallery so later layout passes share one settle.
      const flkty=slider.flkty;
      let hold=gallery.__dlDirectPhotoHeightHold;
      if(hold && hold.flkty!==flkty){
        gallery.__dlDirectPhotoHeightHold=null;
        hold=null;
      }
      if(flkty && (flkty.isAnimating || flkty.isDragging) && typeof flkty.once==='function'){
        if(!hold){
          hold={flkty,heldHeight:0};
          gallery.__dlDirectPhotoHeightHold=hold;
          flkty.once('settle',()=>{
            if(gallery.__dlDirectPhotoHeightHold!==hold) return;
            gallery.__dlDirectPhotoHeightHold=null;
            if(hold.release) hold.release();
          });
        }
        hold.heldHeight=Math.max(hold.heldHeight,previousViewportHeight,heroH);
        hold.targetHeight=heroH;
        hold.selectedSlide=currentImageSlide;
        hold.galleryPass=galleryPass;
        hold.release=()=>{
          if(!isCurrentPhoto() || layoutRevision!==photoLayoutRevision || slider.flkty!==flkty) return;
          writePhotoHeight(hold.targetHeight);
        };
      }else{
        gallery.__dlDirectPhotoHeightHold=null;
        hold=null;
      }
      writePhotoHeight(hold ? hold.heldHeight : heroH);

      [60,220,600].forEach(ms=>setTimeout(()=>{
        try{
          if(!isCurrentPhoto() || layoutRevision!==photoLayoutRevision) return;
          const activeHold=gallery.__dlDirectPhotoHeightHold;
          const height=activeHold && activeHold.galleryPass===galleryPass &&
            activeHold.selectedSlide===currentImageSlide && activeHold.flkty===slider.flkty
              ? activeHold.heldHeight : heroH;
          flickViewport.style.setProperty('height',height+'px','important');
          slider.style.setProperty('height',height+'px','important');
          imageContainer.style.setProperty('height',height+'px','important');
        }catch(e){}
      },ms));
    };

    const begin=async()=>{
      if(!isCurrentPhoto() || !img.naturalWidth || !img.naturalHeight) return;
      const bounds=r.classList.contains('daluca-pdp--ready-leather-military')
        ? fullBounds
        : (dlDirectGalleryBounds(img) || fullBounds);

      applyPhotoLayout(bounds);
      await dlPromoteShopifyImage(img,sourceRequest);
      if(!isCurrentPhoto()) return;
      if(img.complete && img.naturalWidth){
        applyPhotoLayout(bounds);
      }else{
        img.addEventListener('load',()=>applyPhotoLayout(bounds),{once:true});
      }
    };

    if(img.complete && img.naturalWidth){
      begin();
    }else{
      applyPhotoLayout(fullBounds);
      img.addEventListener('load',begin,{once:true});
    }
  }

  function dlScheduleDirectGallery(){
    [0,80,250,700].forEach(ms=>setTimeout(()=>{
      try{ dlApplyDirectGallery(); }catch(e){}
    },ms));
  }

  if(document.readyState==='loading'){
    document.addEventListener('DOMContentLoaded',dlScheduleDirectGallery,{once:true});
  }else{
    dlScheduleDirectGallery();
  }

  if(!window.__dalucaDirectGalleryResize){
    window.__dalucaDirectGalleryResize=true;
    window.addEventListener('resize',()=>{
      clearTimeout(window.__dalucaDirectGalleryResizeTimer);
      window.__dalucaDirectGalleryResizeTimer=setTimeout(dlApplyDirectGallery,140);
    },{passive:true});
  }

  // Re-run after Flickity navigation / thumbnail clicks.
  document.addEventListener('click',e=>{
    if(e.target && e.target.closest('.product-gallery')){
      setTimeout(dlApplyDirectGallery,90);
      setTimeout(dlApplyDirectGallery,260);
    }
  });


  /* =========================================================
     DESKTOP LIGHTBOX — CLEAN CLICK-TO-INSPECT
     - No hover zoom on the PDP.
     - Click the hero image or existing zoom control to open.
     - Wheel / +/- zoom inside lightbox.
     - Drag to pan when zoomed.
     - ESC closes.
     - Mobile remains untouched so its own tap/pinch behavior can be handled separately.
     ========================================================= */

  function dlGetActiveGalleryImage(){
    const r=root();
    if(!r) return null;
    const gallery=r.querySelector('.product-gallery');
    if(!gallery) return null;

    const selected=gallery.querySelector(
      '.product-images__slide.is-selected .product-single__media-image img,' +
      '.product-images__slide.is-selected img,' +
      '.product-images__slide.is-active .product-single__media-image img,' +
      '.product-images__slide.is-active img'
    );
    return selected || gallery.querySelector('.product-single__media-image img');
  }

  function dlBestLightboxSrc(img){
    if(!img) return '';

    const naturalTarget=Math.max(
      1000,
      Math.min(
        1600,
        Number(img.getAttribute('width')) || img.naturalWidth || 1200
      )
    );

    let src=img.currentSrc || img.src || '';
    if(!src) return '';

    // Ask Shopify for a large inspection image without inventing a source beyond
    // the actual underlying asset. Shopify will serve the best available size.
    if(/([?&])width=\d+/i.test(src)){
      src=src.replace(/([?&])width=\d+/i,'$1width='+naturalTarget);
    }else{
      src += (src.includes('?')?'&':'?')+'width='+naturalTarget;
    }

    return src;
  }

  function dlCreateLightbox(){
    if(document.querySelector('.daluca-pdp-lightbox')) {
      return document.querySelector('.daluca-pdp-lightbox');
    }

    const box=document.createElement('div');
    box.className='daluca-pdp-lightbox';
    box.setAttribute('aria-hidden','true');

    window.DaLucaI18n.setHtml(box, `
      <div class="daluca-pdp-lightbox__backdrop" data-dl-close></div>
      <div class="daluca-pdp-lightbox__panel" role="dialog" aria-modal="true" aria-label="Product image viewer">
        <button class="daluca-pdp-lightbox__close" type="button" aria-label="Close image viewer" data-dl-close>×</button>

        <div class="daluca-pdp-lightbox__stage">
          <img class="daluca-pdp-lightbox__image" alt="">
        </div>

        <div class="daluca-pdp-lightbox__controls" aria-label="Image controls">
          <button type="button" data-dl-zoom-out aria-label="Zoom out">−</button>
          <button type="button" data-dl-reset aria-label="Reset zoom">100%</button>
          <button type="button" data-dl-zoom-in aria-label="Zoom in">+</button>
        </div>
      </div>
    `);

    document.body.appendChild(box);

    const stage=box.querySelector('.daluca-pdp-lightbox__stage');
    const image=box.querySelector('.daluca-pdp-lightbox__image');
    const reset=box.querySelector('[data-dl-reset]');

    let scale=1;
    let x=0;
    let y=0;
    let dragging=false;
    let startX=0;
    let startY=0;
    let originX=0;
    let originY=0;

    const clamp=(v,min,max)=>Math.max(min,Math.min(max,v));

    const render=()=>{
      scale=clamp(scale,1,3);
      if(scale===1){ x=0; y=0; }
      image.style.transform=`translate3d(${x}px,${y}px,0) scale(${scale})`;
      image.style.cursor=scale>1?(dragging?'grabbing':'grab'):'zoom-in';
      window.DaLucaI18n.setText(reset, Math.round(scale*100)+'%');
      box.classList.toggle('is-zoomed',scale>1);
    };

    const setScale=(next)=>{
      const old=scale;
      scale=clamp(next,1,3);
      if(scale===1){
        x=0; y=0;
      }else if(old!==scale){
        x*=scale/old;
        y*=scale/old;
      }
      render();
    };

    const close=()=>{
      box.classList.remove('is-open');
      box.setAttribute('aria-hidden','true');
      document.documentElement.classList.remove('daluca-lightbox-open');
      document.body.classList.remove('daluca-lightbox-open');
      scale=1; x=0; y=0; dragging=false;
      render();
    };

    box.querySelectorAll('[data-dl-close]').forEach(el=>el.addEventListener('click',close));
    box.querySelector('[data-dl-zoom-in]').addEventListener('click',()=>setScale(scale+.25));
    box.querySelector('[data-dl-zoom-out]').addEventListener('click',()=>setScale(scale-.25));
    reset.addEventListener('click',()=>setScale(1));

    stage.addEventListener('wheel',e=>{
      if(!box.classList.contains('is-open')) return;
      e.preventDefault();
      setScale(scale + (e.deltaY<0 ? .2 : -.2));
    },{passive:false});

    stage.addEventListener('dblclick',e=>{
      e.preventDefault();
      setScale(scale===1 ? 1.75 : 1);
    });

    stage.addEventListener('pointerdown',e=>{
      if(scale<=1) return;
      dragging=true;
      startX=e.clientX;
      startY=e.clientY;
      originX=x;
      originY=y;
      stage.setPointerCapture?.(e.pointerId);
      render();
    });

    stage.addEventListener('pointermove',e=>{
      if(!dragging || scale<=1) return;
      x=originX+(e.clientX-startX);
      y=originY+(e.clientY-startY);
      render();
    });

    const stopDrag=()=>{
      if(!dragging) return;
      dragging=false;
      render();
    };
    stage.addEventListener('pointerup',stopDrag);
    stage.addEventListener('pointercancel',stopDrag);
    stage.addEventListener('pointerleave',stopDrag);

    document.addEventListener('keydown',e=>{
      if(!box.classList.contains('is-open')) return;
      if(e.key==='Escape') close();
      if(e.key==='+' || e.key==='=') setScale(scale+.25);
      if(e.key==='-') setScale(scale-.25);
      if(e.key==='0') setScale(1);
    });

    box._dlOpen=(img)=>{
      const src=dlBestLightboxSrc(img);
      if(!src) return;

      image.alt=img.alt || 'Product image';
      image.src=src;

      scale=1; x=0; y=0; dragging=false;
      render();

      box.classList.add('is-open');
      box.setAttribute('aria-hidden','false');
      document.documentElement.classList.add('daluca-lightbox-open');
      document.body.classList.add('daluca-lightbox-open');
    };

    return box;
  }

  function dlBindDesktopLightbox(){
    if(window.innerWidth<768) return;

    const r=root();
    if(!r) return;

    const gallery=r.querySelector('.product-gallery');
    if(!gallery) return;

    const box=dlCreateLightbox();

    if(!gallery.dataset.dalucaLightboxBound){
      gallery.dataset.dalucaLightboxBound='true';

      gallery.addEventListener('click',e=>{
        if(window.innerWidth<768) return;

        // Existing zoom control or a click directly on the main hero image.
        const zoomControl=e.target.closest(
          '[data-daluca-zoom], .daluca-gallery__zoom, .product-gallery__zoom, .product-image-zoom, button[aria-label*="zoom" i], a[aria-label*="zoom" i]'
        );

        const heroImg=e.target.closest(
          '.product-images__slide.is-selected .product-single__media-image img,' +
          '.product-images__slide.is-active .product-single__media-image img,' +
          '.product-single__media-image img'
        );

        if(!zoomControl && !heroImg) return;

        e.preventDefault();
        e.stopPropagation();

        const img=dlGetActiveGalleryImage();
        if(img) box._dlOpen(img);
      },true);
    }
  }

  function dlScheduleDesktopLightbox(){
    [0,100,350,900].forEach(ms=>setTimeout(()=>{
      try{ dlBindDesktopLightbox(); }catch(e){}
    },ms));
  }

  if(document.readyState==='loading'){
    document.addEventListener('DOMContentLoaded',dlScheduleDesktopLightbox,{once:true});
  }else{
    dlScheduleDesktopLightbox();
  }

  if(!window.__dalucaDesktopLightboxResize){
    window.__dalucaDesktopLightboxResize=true;
    window.addEventListener('resize',()=>{
      clearTimeout(window.__dalucaDesktopLightboxResizeTimer);
      window.__dalucaDesktopLightboxResizeTimer=setTimeout(dlBindDesktopLightbox,120);
    },{passive:true});
  }


  /* =========================================================
     PEWHL / NON-VINTAGE ADD-ONS — EXACT LIVE DOM NORMALIZER
     Live markup confirmed in Firefox:
       .daluca-ready-essential-cards-row
       product-card.daluca-ready-essential-card
       .daluca-pdp-upsell__image
       .daluca-pdp-upsell__content
       button.daluca-pdp-upsell__add
       span.daluca-pdp-upsell__add-label
       button.daluca-pdp-upsells__bundle.daluca-ready-add-all-control
     ========================================================= */
  function dlNormalizeNonVintageEssentials(){
    const r=root();
    if(!r || isVintageReady(r)) return;

    const e=r.querySelector('.daluca-pdp-upsells--essentials.daluca-ready-essentials-grid');
    if(!e) return;

    e.classList.add('daluca-ready-essentials--exact');

    const row=e.querySelector(':scope > .daluca-ready-essential-cards-row');
    if(row) row.classList.add('daluca-ready-essential-cards-row--exact');

    e.querySelectorAll('product-card.daluca-ready-essential-card').forEach(card=>{
      card.classList.add('daluca-ready-essential-card--exact');

      const add=card.querySelector(':scope > button.daluca-pdp-upsell__add');
      if(add){
        add.classList.add('daluca-ready-essential-add--exact');

        const label=add.querySelector('.daluca-pdp-upsell__add-label');
        if(label) label.classList.add('daluca-ready-essential-add-label--exact');

        // Remove meaningless separator/spinner text nodes that can render
        // as a slash between price and Add +.
        Array.from(add.childNodes).forEach(node=>{
          if(node.nodeType===Node.TEXT_NODE){
            const t=String(node.nodeValue || '').trim();
            if(t==='/' || t==='\\' || t==='|') node.remove();
          }
        });
      }
    });

    const bundle=e.querySelector(
      ':scope > button.daluca-pdp-upsells__bundle.daluca-ready-add-all-control'
    );

    if(bundle && !bundle.dataset.dalucaExactBundle){
      const raw=clean(window.DaLucaI18n.sourceText(bundle));
      const priceMatch=raw.match(/(\$[\d,.]+(?:\s*USD)?)\s*$/i);
      const price=priceMatch ? priceMatch[1] : '';
      let middle=raw
        .replace(/^add all three\s*/i,'')
        .replace(/(\$[\d,.]+(?:\s*USD)?)\s*$/i,'')
        .trim();

      if(!middle) middle='Cream, shine cloth and spring bar tool';

      // Keep the button itself (and therefore its existing click handler),
      // but give the visible content a stable premium layout.
      window.DaLucaI18n.setText(bundle, '');

      const copy=document.createElement('span');
      copy.className='daluca-ready-bundle-copy--exact';

      const title=document.createElement('strong');
      title.className='daluca-ready-bundle-title--exact';
      window.DaLucaI18n.setText(title, 'Add all three');

      const desc=document.createElement('small');
      desc.className='daluca-ready-bundle-desc--exact';
      window.DaLucaI18n.setText(desc, middle);

      copy.appendChild(title);
      copy.appendChild(desc);
      bundle.appendChild(copy);

      if(price){
        const p=document.createElement('span');
        p.className='daluca-ready-bundle-price--exact';
        window.DaLucaI18n.setText(p, price);
        bundle.appendChild(p);
      }

      bundle.dataset.dalucaExactBundle='true';
    }
  }

  function dlScheduleNonVintageEssentials(){
    [0,80,260,700].forEach(ms=>setTimeout(()=>{
      try{ dlNormalizeNonVintageEssentials(); }catch(e){}
    },ms));
  }

  if(document.readyState==='loading'){
    document.addEventListener('DOMContentLoaded',dlScheduleNonVintageEssentials,{once:true});
  }else{
    dlScheduleNonVintageEssentials();
  }

  document.addEventListener('click',e=>{
    if(e.target && e.target.closest('.daluca-pdp-upsells--essentials')){
      setTimeout(dlNormalizeNonVintageEssentials,120);
      setTimeout(dlNormalizeNonVintageEssentials,400);
    }
  });


  /* =========================================================
     NON-VINTAGE ADD-ONS — FORCED FINAL LAYOUT
     The live diagnostic confirmed the new code was running, but older
     high-specificity !important rules were still winning the cascade.
     This pass applies the final add-on geometry directly to the exact
     live elements with inline !important declarations.
     ========================================================= */
  window.__dalucaNonVintageAddonBuild='final-polish-438';

  function dlForceNonVintageAddonLayout(){
    const r=root();
    if(!r || r.dataset.dalucaProductAvailable==='false' || isVintageReady(r) || window.innerWidth < 768) return;

    const essentials=r.querySelector(
      '.daluca-pdp-upsells--essentials.daluca-ready-essentials-grid'
    );
    if(!essentials) return;

    const row=essentials.querySelector(':scope > .daluca-ready-essential-cards-row');
    const cards=Array.from(
      essentials.querySelectorAll(
        ':scope > .daluca-ready-essential-cards-row > product-card.daluca-ready-essential-card'
      )
    );
    const bundle=essentials.querySelector(
      ':scope > button.daluca-pdp-upsells__bundle.daluca-ready-add-all-control'
    );

    // V4.20: keep the intended 438px desktop visual floor, but NEVER lock
    // the purchase row to a fixed height. Box 03 must be free to grow from
    // its actual add-on content; CSS Grid then stretches 01/02/04 to match.
    // This replaces the old fixed 438px inline !important lock.
    const steps=r.querySelector('.daluca-ready-steps:not(.daluca-ready-steps--vintage)');
    if(steps && window.innerWidth >= 1181){
      steps.style.setProperty('height','auto','important');
      steps.style.setProperty('min-height','0','important');
      steps.style.setProperty('max-height','none','important');
      steps.style.setProperty('align-items','stretch','important');
      Array.from(steps.children).forEach(step=>{
        if(
          step.classList.contains('daluca-concierge-step') ||
          step.classList.contains('daluca-ready-step--setup')
        ){
          step.style.setProperty('height','auto','important');
          step.style.setProperty('min-height','438px','important');
          step.style.setProperty('max-height','none','important');
          step.style.setProperty('align-self','stretch','important');
          step.style.setProperty('overflow','visible','important');
        }
      });
    }

    essentials.style.setProperty('display','block','important');
    essentials.style.setProperty('width','100%','important');
    essentials.style.setProperty('margin-top','6px','important');
    essentials.style.setProperty('padding','0','important');
    essentials.style.setProperty('overflow','visible','important');

    if(row){
      row.style.setProperty('display','grid','important');
      row.style.setProperty('grid-template-columns','repeat(3,minmax(0,1fr))','important');
      row.style.setProperty('gap','8px','important');
      row.style.setProperty('align-items','stretch','important');
      row.style.setProperty('width','100%','important');
      row.style.setProperty('margin','0 0 12px','important');
      row.style.setProperty('padding','0','important');
      row.style.setProperty('overflow','visible','important');
    }

    cards.forEach(card=>{
      card.style.setProperty('box-sizing','border-box','important');
      card.style.setProperty('display','flex','important');
      card.style.setProperty('flex-direction','column','important');
      card.style.setProperty('align-items','center','important');
      card.style.setProperty('justify-content','flex-start','important');
      card.style.setProperty('width','100%','important');
      card.style.setProperty('height','145px','important');
      card.style.setProperty('min-height','145px','important');
      card.style.setProperty('max-height','145px','important');
      card.style.setProperty('margin','0','important');
      card.style.setProperty('padding','10px 9px 9px','important');
      card.style.setProperty('gap','4px','important');
      card.style.setProperty('overflow','hidden','important');
      card.style.setProperty('background','#fff','important');

      const image=card.querySelector(':scope > .daluca-pdp-upsell__image');
      if(image){
        image.style.setProperty('display','flex','important');
        image.style.setProperty('align-items','center','important');
        image.style.setProperty('justify-content','center','important');
        image.style.setProperty('flex','0 0 44px','important');
        image.style.setProperty('width','44px','important');
        image.style.setProperty('height','44px','important');
        image.style.setProperty('min-width','44px','important');
        image.style.setProperty('min-height','44px','important');
        image.style.setProperty('max-width','44px','important');
        image.style.setProperty('max-height','44px','important');
        image.style.setProperty('margin','0 auto 2px','important');
        image.style.setProperty('padding','0','important');

        const img=image.querySelector('img');
        if(img){
          img.style.setProperty('width','100%','important');
          img.style.setProperty('height','100%','important');
          img.style.setProperty('max-width','44px','important');
          img.style.setProperty('max-height','44px','important');
          img.style.setProperty('object-fit','contain','important');
        }
      }

      const content=card.querySelector(':scope > .daluca-pdp-upsell__content');
      if(content){
        content.style.setProperty('display','flex','important');
        content.style.setProperty('flex','1 1 auto','important');
        content.style.setProperty('flex-direction','column','important');
        content.style.setProperty('align-items','center','important');
        content.style.setProperty('justify-content','flex-start','important');
        content.style.setProperty('width','100%','important');
        content.style.setProperty('min-height','0','important');
        content.style.setProperty('margin','0','important');
        content.style.setProperty('padding','0','important');
        content.style.setProperty('gap','2px','important');
        content.style.setProperty('overflow','visible','important');
        content.style.setProperty('text-align','center','important');

        content.querySelectorAll('h1,h2,h3,h4,h5,h6,strong').forEach(el=>{
          el.style.setProperty('margin','0','important');
          el.style.setProperty('font-size','10px','important');
          el.style.setProperty('line-height','1.14','important');
          el.style.setProperty('text-align','center','important');
        });

        content.querySelectorAll('p,small').forEach(el=>{
          el.style.setProperty('display','block','important');
          el.style.setProperty('visibility','visible','important');
          el.style.setProperty('opacity','1','important');
          el.style.setProperty('margin','0','important');
          el.style.setProperty('font-size','9px','important');
          el.style.setProperty('line-height','1.16','important');
          el.style.setProperty('text-align','center','important');
        });

        content.querySelectorAll('.price,.money,[class*="price"]').forEach(el=>{
          el.style.setProperty('display','block','important');
          el.style.setProperty('visibility','visible','important');
          el.style.setProperty('opacity','1','important');
          el.style.setProperty('position','static','important');
          el.style.setProperty('margin','2px 0 0','important');
          el.style.setProperty('padding','0','important');
          el.style.setProperty('font-size','13px','important');
          el.style.setProperty('line-height','1.2','important');
          el.style.setProperty('font-weight','700','important');
          el.style.setProperty('text-align','center','important');
        });
      }

      const add=card.querySelector(':scope > button.daluca-pdp-upsell__add');
      if(add){
        // Remove separator/spinner artifacts and class pollution on descendants.
        add.querySelectorAll('.daluca-ready-add-all-control').forEach(el=>{
          el.classList.remove('daluca-ready-add-all-control');
        });

        Array.from(add.childNodes).forEach(node=>{
          if(node.nodeType===Node.TEXT_NODE){
            const t=String(node.nodeValue || '').trim();
            if(t==='/' || t==='\\' || t==='|') node.remove();
          }
        });

        add.style.setProperty('box-sizing','border-box','important');
        add.style.setProperty('position','static','important');
        add.style.setProperty('display','flex','important');
        add.style.setProperty('align-items','center','important');
        add.style.setProperty('justify-content','center','important');
        add.style.setProperty('flex','0 0 22px','important');
        add.style.setProperty('width','auto','important');
        add.style.setProperty('height','22px','important');
        add.style.setProperty('min-height','22px','important');
        add.style.setProperty('max-height','22px','important');
        add.style.setProperty('margin','2px auto 0','important');
        add.style.setProperty('padding','3px 9px 2px','important');
        add.style.setProperty('border','0','important');
        add.style.setProperty('background','transparent','important');
        add.style.setProperty('color','#223a56','important');
        add.style.setProperty('font-size','12.5px','important');
        add.style.setProperty('line-height','1.15','important');
        add.style.setProperty('font-weight','600','important');
        add.style.setProperty('text-align','center','important');
        add.style.setProperty('text-decoration','underline','important');
        add.style.setProperty('overflow','visible','important');

        const label=add.querySelector('.daluca-pdp-upsell__add-label');
        if(label){
          label.style.setProperty('display','inline','important');
          label.style.setProperty('position','static','important');
          label.style.setProperty('width','auto','important');
          label.style.setProperty('height','auto','important');
          label.style.setProperty('margin','0','important');
          label.style.setProperty('padding','0','important');
          label.style.setProperty('font','inherit','important');
          label.style.setProperty('line-height','inherit','important');
          label.style.setProperty('text-decoration','inherit','important');
        }
      }
    });

    if(bundle){
      // Some older shared logic can accidentally add this class to the inner
      // "Add all three" title. Strip it every time before styling.
      bundle.querySelectorAll('.daluca-ready-add-all-control').forEach(el=>{
        el.classList.remove('daluca-ready-add-all-control');
      });

      bundle.style.setProperty('box-sizing','border-box','important');
      bundle.style.setProperty('display','grid','important');
      bundle.style.setProperty('grid-template-columns','minmax(0,1fr) auto','important');
      bundle.style.setProperty('align-items','center','important');
      bundle.style.setProperty('gap','18px','important');
      bundle.style.setProperty('width','100%','important');
      bundle.style.setProperty('height','40px','important');
      bundle.style.setProperty('min-height','40px','important');
      bundle.style.setProperty('max-height','40px','important');
      bundle.style.setProperty('margin','0','important');
      bundle.style.setProperty('padding','8px 18px 8px 12px','important');
      bundle.style.setProperty('border','0','important');
      bundle.style.setProperty('background','#223a56','important');
      bundle.style.setProperty('color','#fff','important');
      bundle.style.setProperty('overflow','hidden','important');
      bundle.style.setProperty('text-align','left','important');

      const copy=bundle.querySelector('.daluca-ready-bundle-copy--exact');
      const title=bundle.querySelector('.daluca-ready-bundle-title--exact');
      const desc=bundle.querySelector('.daluca-ready-bundle-desc--exact');
      const price=bundle.querySelector('.daluca-ready-bundle-price--exact');

      if(copy){
        copy.style.setProperty('display','flex','important');
        copy.style.setProperty('flex-direction','column','important');
        copy.style.setProperty('align-items','flex-start','important');
        copy.style.setProperty('justify-content','center','important');
        copy.style.setProperty('min-width','0','important');
        copy.style.setProperty('margin','0','important');
        copy.style.setProperty('padding','0','important');
      }
      if(title){
        title.classList.remove('daluca-ready-add-all-control');
        title.style.setProperty('display','block','important');
        title.style.setProperty('margin','0','important');
        title.style.setProperty('font-size','12.5px','important');
        title.style.setProperty('line-height','1.05','important');
        title.style.setProperty('font-weight','700','important');
        title.style.setProperty('color','#fff','important');
      }
      if(desc){
        desc.style.setProperty('display','block','important');
        desc.style.setProperty('margin','3px 0 0','important');
        desc.style.setProperty('font-size','10.5px','important');
        desc.style.setProperty('line-height','1.08','important');
        desc.style.setProperty('color','rgba(255,255,255,.88)','important');
        desc.style.setProperty('white-space','nowrap','important');
        desc.style.setProperty('overflow','hidden','important');
        desc.style.setProperty('text-overflow','ellipsis','important');
      }
      if(price){
        price.style.setProperty('display','block','important');
        price.style.setProperty('justify-self','end','important');
        price.style.setProperty('margin','0','important');
        price.style.setProperty('font-size','12.5px','important');
        price.style.setProperty('line-height','1','important');
        price.style.setProperty('font-weight','700','important');
        price.style.setProperty('color','#fff','important');
        price.style.setProperty('white-space','nowrap','important');
      }
    }

    essentials.querySelectorAll(
      ':scope > .daluca-pdp-upsells__list:empty, :scope > .daluca-pdp-upsells__bundle-message:empty'
    ).forEach(el=>{
      el.style.setProperty('display','none','important');
      el.style.setProperty('height','0','important');
      el.style.setProperty('margin','0','important');
      el.style.setProperty('padding','0','important');
    });
  }

  function dlScheduleForcedNonVintageAddons(){
    [0,120,350,800,1500].forEach(ms=>setTimeout(()=>{
      try{ dlForceNonVintageAddonLayout(); }catch(e){}
    },ms));
  }

  if(document.readyState==='loading'){
    document.addEventListener('DOMContentLoaded',dlScheduleForcedNonVintageAddons,{once:true});
  }else{
    dlScheduleForcedNonVintageAddons();
  }

  // Shared purchase code can re-touch this block after selections/cart state.
  // Reassert only this small area after relevant clicks.
  document.addEventListener('click',e=>{
    if(e.target && e.target.closest('.daluca-ready-step--setup')){
      setTimeout(dlForceNonVintageAddonLayout,80);
      setTimeout(dlForceNonVintageAddonLayout,280);
    }
  });


  /* =========================================================
     NON-VINTAGE ADD-ON PRICE FIX
     The screenshot confirmed the only remaining card defect:
     the accessory PRICE line is being vertically clipped by the old
     nested product-card price markup.

     Instead of fighting that inherited markup, normalize only the visible
     content area into three simple rows:
       title
       description
       price
     The image link and original Add + button are preserved.
     ========================================================= */
  function dlNormalizeAddonCardCopy(){
    const r=root();
    if(!r || isVintageReady(r)) return;

    const essentials=r.querySelector(
      '.daluca-pdp-upsells--essentials.daluca-ready-essentials-grid'
    );
    if(!essentials) return;

    essentials.querySelectorAll(
      '.daluca-ready-essential-cards-row > product-card.daluca-ready-essential-card'
    ).forEach(card=>{
      const content=card.querySelector(':scope > .daluca-pdp-upsell__content');
      const image=card.querySelector(':scope > a.daluca-pdp-upsell__image');
      if(!content || content.dataset.dalucaCopyNormalized==='true') return;

      const full=clean(window.DaLucaI18n.sourceText(content));
      const priceMatch=full.match(/(\$[\d,.]+(?:\s*USD)?)\s*$/i);
      const price=priceMatch ? priceMatch[1] : '';

      let title='';
      const titleNode=content.querySelector('a,h1,h2,h3,h4,h5,h6,strong');
      if(titleNode) title=clean(window.DaLucaI18n.sourceText(titleNode));

      // innerText generally preserves the original product-card line breaks,
      // which is the safest fallback for the title.
      if(!title){
        const lines=String(content.innerText || '')
          .split(/\n+/)
          .map(clean)
          .filter(Boolean)
          .filter(t=>!price || t!==price);
        if(lines.length) title=lines[0];
      }

      if(!title){
        // Known fallback: use the first phrase before the description after
        // removing the trailing price.
        const withoutPrice=price ? full.replace(price,'').trim() : full;
        title=withoutPrice.slice(0,80);
      }

      let description='';
      const descNode=Array.from(
        content.querySelectorAll('p,small,[class*="description"],[class*="subtitle"]')
      ).find(el=>{
        const t=clean(window.DaLucaI18n.sourceText(el));
        return t && t!==title && (!price || !t.includes(price));
      });

      if(descNode) description=clean(window.DaLucaI18n.sourceText(descNode));

      if(!description){
        let remainder=full;
        if(title) remainder=remainder.replace(title,'').trim();
        if(price) remainder=remainder.replace(price,'').trim();
        description=remainder;
      }

      // Rebuild only the passive copy area.  The card image and Add button
      // remain untouched, so product navigation/cart behavior stays intact.
      window.DaLucaI18n.setText(content, '');
      content.classList.add('daluca-addon-copy-clean');

      let titleEl;
      if(image && image.href){
        titleEl=document.createElement('a');
        titleEl.href=image.href;
      }else{
        titleEl=document.createElement('span');
      }
      titleEl.className='daluca-addon-title-clean';
      window.DaLucaI18n.setText(titleEl, title || 'Accessory');
      content.appendChild(titleEl);

      if(description){
        const desc=document.createElement('span');
        desc.className='daluca-addon-desc-clean';
        window.DaLucaI18n.setText(desc, description);
        content.appendChild(desc);
      }

      if(price){
        const p=document.createElement('span');
        p.className='daluca-addon-price-clean';
        window.DaLucaI18n.setText(p, price);
        content.appendChild(p);
      }

      content.dataset.dalucaCopyNormalized='true';
    });
  }

  function dlScheduleAddonCopyFix(){
    [0,100,320,800,1500].forEach(ms=>setTimeout(()=>{
      try{
        dlNormalizeAddonCardCopy();
        if(typeof dlForceNonVintageAddonLayout==='function'){
          dlForceNonVintageAddonLayout();
        }
      }catch(e){}
    },ms));
  }

  if(document.readyState==='loading'){
    document.addEventListener('DOMContentLoaded',dlScheduleAddonCopyFix,{once:true});
  }else{
    dlScheduleAddonCopyFix();
  }

  document.addEventListener('click',e=>{
    if(e.target && e.target.closest('.daluca-ready-step--setup')){
      setTimeout(dlNormalizeAddonCardCopy,100);
      setTimeout(dlNormalizeAddonCardCopy,350);
    }
  });

  function build(){
    const r=root();
    if(!r || r.dataset.dalucaReadyBuilt==='true') return;

    const gallery=r.querySelector(':scope > .product-gallery') || r.querySelector('.product-gallery');
    const infoWrap=r.querySelector(':scope > .product-information') || r.querySelector('.product-information');

    // Critical vintage fix:
    // Some vintage products do NOT render .product-information--inner.
    // Falling back to the outer Product Information wrapper (or the ready-made
    // root itself) prevents the organizer from exiting before vintage logic runs.
    const info=(infoWrap && (infoWrap.querySelector('.product-information--inner') || infoWrap)) || r;

    if(!gallery) return;

    const holder=steps(r,info);
    if(!holder) return;
    let ss=storySpecs(r,info,holder);

    // Vintage markup can differ enough that its editorial row needs the
    // dedicated root-level repair before we give up.
    if(!ss && isVintageReady(r)){
      applyVintageReadyVariant(r);
      repairVintageInlineSpecs(r);
      ss=r.querySelector('.daluca-ready-story-specs');
    }

    if(!ss) return;

    if(r.firstElementChild!==gallery) r.insertBefore(gallery,r.firstElementChild);
    r.insertBefore(ss,holder);

    applyVintageReadyVariant(r);
    repairVintageInlineSpecs(r);
    r.dataset.dalucaReadyBuilt='true';
    setTimeout(()=>{
      try{
        polishLowerWorkshop();
        applyVintageReadyVariant(r);
        repairVintageInlineSpecs(r);
      }catch(e){}
    },80);
  }

  if(document.readyState==='loading') document.addEventListener('DOMContentLoaded',build); else build();
  let tries=0;
  const timer=setInterval(()=>{
    tries+=1; build();
    try{
      const rr0=root();
      polishLowerWorkshop();
      applyVintageReadyVariant(rr0);
      repairVintageInlineSpecs(rr0);
    }catch(e){}
    try{
      const rr=root();
      if(rr){
        const box=rr.querySelector('.daluca-ready-step--setup .daluca-concierge-step__body') || rr.querySelector('.daluca-ready-step--setup');
        const essentials=rr.querySelector('.daluca-ready-step--setup .daluca-pdp-upsells--essentials');
        if(box){
          box.dataset.dalucaSetupCompacted='';
          compactSetupBox(rr,box,essentials);
        }
      }
    }catch(e){}
    const r=root();
    if((r && r.dataset.dalucaReadyBuilt==='true') || tries>30) clearInterval(timer);
  },250);
})();




(function(){
  const requested=new URLSearchParams(location.search).get('leather_color');
  if(!requested)return;
  let observer;
  function choose(){
    const input=Array.from(document.querySelectorAll('.gpo-app input[name="Leather Color"]')).find(el=>el.value===requested);
    if(!input)return false;
    if(!input.checked){input.checked=true;input.dispatchEvent(new Event('change',{bubbles:true}));}
    if(observer)observer.disconnect();
    return true;
  }
  function boot(){if(choose())return;observer=new MutationObserver(choose);observer.observe(document.body,{childList:true,subtree:true});setTimeout(()=>observer.disconnect(),20000);}
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',boot,{once:true});else boot();
})();


